import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import '@google/model-viewer';
import { apiClient } from '../api/client';
import { getCollectionPointsDistribution, type PointsDistributionResult } from '../api/richlist';
import { is3DModel, getModelViewerUrl } from '../utils/media';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { useNFT, useNFTOffers, useNFTHistory, nftQueryKeys } from '../hooks/useNFTDetail';
import { Avatar } from '../ui/components/Avatar';
import { Badge } from '../ui/components/Badge';
import { Button } from '../ui/components/Button';
import { Tooltip } from '../ui/components/Tooltip';
import { IconButton } from '../ui/components/IconButton';
import { Loader } from '../ui/components/Loader';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { IconWithBackground } from '../ui/components/IconWithBackground';
import { Table } from '../ui/components/Table';
import { TextField } from '../ui/components/TextField';
import { Select } from '../ui/components/Select';
import { DropdownMenu } from '../ui/components/DropdownMenu';
import { Alert } from '../ui/components/Alert';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { sseConnection } from '../services/SSEConnection';
import { useSSEStore } from '../store/sseStore';
import { XamanModal } from '../components/auth/XamanModal';
import { XamanTransactionModal, type TransactionStage } from '../components/transactions/XamanTransactionModal';
import type { TransactionFees, NFTDetails } from '../components/transactions/TransactionConfirmation';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import { Tabs } from '../ui/components/Tabs';
import { AuctionListingForm } from '../components/auctions/AuctionListingForm';
import { ActiveAuctionView } from '../components/auctions/ActiveAuctionView';
import { useActiveAuction } from '../hooks/useActiveAuction';
import { BlurredPremiumFeature } from '../components/BlurredPremiumFeature';
import { RarityRank } from '../components/nft/RarityRank';
import { formatPoints } from '../components/nft/PointsBadge';
import { MetadataHistoryModal } from '../components/nft/MetadataHistoryModal';
import { ExpandableDescription } from '../components/nft/ExpandableDescription';
import { CreatorToolsSection } from '../components/nft/CreatorToolsSection';
import type { MutationResult } from '../api/mutations';
import type { NFT, FormattedOffer } from '../types';
import { formatDistanceToNow, formatDistance, format } from 'date-fns';
import { getLowestSellOffer } from '../types/offer';
import { formatPrice, formatNumber, getRarityInfo, createTraitRarityLookup } from '../utils/formatters';
import { buildShareableUrl } from '../utils/referral';
import {
  FeatherArrowLeftRight,
  FeatherAward,
  FeatherCheck,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherCopy,
  FeatherDollarSign,
  FeatherEdit2,
  FeatherFlag,
  FeatherInfo,
  FeatherMessageCircle,
  FeatherShare,
  FeatherShoppingBag,
  FeatherShoppingCart,
  FeatherSparkles,
  FeatherStar,
  FeatherTag,
  FeatherUser,
  FeatherVerified,
  FeatherX,
  FeatherZap,
} from '@subframe/core';
import { PromotionDialog } from '../components/promotions/PromotionDialog';
import { getPromotionStatus, type PromotionStatus } from '../api/promotions';
import * as SubframeCore from '@subframe/core';
import {
  getBestMatchingOfferForNFT,
  type CollectionOffer,
} from '../api/collectionOffers';
import { AcceptCollectionOfferModal } from '../components/collection-offers/AcceptCollectionOfferModal';
import { AcceptBuyOfferModal } from '../components/offers/AcceptBuyOfferModal';
import TradeBuilder from '../components/trades/TradeBuilder';
import { submitNFTReport, type ReportType } from '../api/reports';
import { setNftTagValues } from '../api/creator';
import { getNFTFlipOpportunity, type WalletFlipOpportunity } from '../api/flips';
import {
  MARKETPLACE_OPTIONS,
  getMarketplaceOption,
  getMarketplaceFeePercentage,
  getMarketplaceName,
  calculateBuyAmount,
  calculateMarketplaceFee,
  isSupportedMarketplace,
} from '../lib/marketplace-constants';
import { useCartStore, type CartItem } from '../store/cartStore';
import { featureFlags } from '../config/featureFlags';
import { useCollectionSweepstakes, useEligibleOffers } from '../hooks/useSweepstakes';

/**
 * Format time since sale in a succinct format
 * < 1 hour: "5min"
 * < 24 hours: "8h"
 * < 365 days: "2d", "31d", "364d"
 * >= 365 days: ">1y"
 */
function formatTimeSinceSale(dateString: string): string {
  const now = new Date();
  const saleDate = new Date(dateString);
  const diffMs = now.getTime() - saleDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) {
    return `${diffMinutes}min`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  } else if (diffDays < 365) {
    return `${diffDays}d`;
  } else {
    return '>1y';
  }
}

export const NFTDetailPage: React.FC = () => {
  const { tokenId } = useParams<{ tokenId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();
  const cartStore = useCartStore();

  // Fire-and-forget view tracking — one hit per tokenId render. Backend
  // dedupes per viewer over 24h and captures document.referrer so creators
  // can see traffic sources (X, YouTube, Discord, etc.).
  useEffect(() => {
    if (!tokenId) return;
    apiClient
      .post(`/nfts/${tokenId}/view`, { referrer: document.referrer || undefined })
      .catch(() => {
        // Tracking failures are silent — they never affect the user experience.
      });
  }, [tokenId]);

  // React Query hooks for data fetching
  const { data: nftData, isLoading: loading, isError, error: nftError, isFetching: isFetchingNFT } = useNFT(tokenId);
  const { data: offersData, isLoading: loadingOffers, isFetching: isFetchingOffers } = useNFTOffers(tokenId);
  const { data: historyData, isLoading: loadingHistory, isFetching: isFetchingHistory } = useNFTHistory(tokenId);

  // Extract data from hooks
  const nft = nftData?.nft;
  const currentOwnerAcquisition = nftData?.currentOwnerAcquisition;
  const ownerMetrics = nftData?.ownerMetrics;
  const ownerHasAccount = nftData?.ownerHasAccount || false;
  const ownerHasVerifiedEmail = nftData?.ownerHasVerifiedEmail || false;
  const offerControls = nftData?.offerControls || { offersDisabled: false, minOfferPrice: null, isActive: false };
  const effectiveOfferControls = nftData?.effectiveOfferControls || { offersDisabled: false, effectiveMinimum: null, source: 'none' as const, walletMinimum: null, nftMinimum: null, noFloorPrice: false };
  const valuation = nftData?.valuation;
  const customTags = nftData?.customTags || [];
  const canManageCollection = nftData?.canManageCollection || false;
  const collectionTags = nftData?.collectionTags || [];
  const pendingTransferOffer = nftData?.pendingTransferOffer || null;
  const outgoingTransferOffer = nftData?.outgoingTransferOffer || null;
  const sellOffers = offersData?.sellOffers || [];
  const buyOffers = offersData?.buyOffers || [];
  const transactions = historyData?.transactions || [];
  const error = isError ? (nftError?.message || 'Failed to load NFT') : null;

  // Sweepstakes contest eligibility
  const { contestView: sweepstakesContest } = useCollectionSweepstakes(nft?.collection_id);
  const activeContestId = sweepstakesContest?.contest?.status === 'active' ? sweepstakesContest.contest.id : undefined;
  const { eligibleOfferIds } = useEligibleOffers(activeContestId);

  // Current sell price (lowest offer)
  const [currentSellPrice, setCurrentSellPrice] = useState<{
    amount: number;
    offerIndex: string;
    expiresAt?: number;
    destination?: string;
    isMarketplaceBrokered?: boolean;
  } | undefined>(undefined);

  // UI state
  const [processing, setProcessing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Multi-image carousel state: we inspect metadata.properties.files for a Metaplex-style
  // file list and, when there's more than one image, let the user cycle through them.
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  // Aspect ratio of the primary image (slide 1), computed client-side from the
  // <img> onLoad event. Once set, we pin the carousel container to this ratio so
  // navigating to alternates with different shapes doesn't cause page jumps —
  // those slides letterbox inside the fixed box via object-contain.
  const [carouselAspectRatio, setCarouselAspectRatio] = useState<number | null>(null);
  useEffect(() => {
    setCurrentImageIndex(0);
    setCarouselAspectRatio(null);
  }, [tokenId]);

  // Tracks a recently-queued mutation. When a user submits a mutation we can't
  // rely on the normal processing_status polling because the row still reads
  // `completed` until ingestion picks up the on-chain NFTokenModify (a 30–90s
  // window after we return 200). Stashing the pre-mutation metadata_version lets
  // us spin up a dedicated poller that lives until `metadata_version` increments.
  // Persisted in sessionStorage so a mid-flight reload still watches.
  const POST_MUTATION_WATCH_KEY = (id: string) => `xd:pending-mutation:${id}`;
  const MAX_WATCH_MS = 3 * 60 * 1000;
  const [postMutationWatch, setPostMutationWatch] = useState<
    { fromVersion: number; startedAt: number } | null
  >(() => {
    if (!tokenId || typeof window === 'undefined') return null;
    try {
      const raw = window.sessionStorage.getItem(POST_MUTATION_WATCH_KEY(tokenId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { fromVersion: number; startedAt: number };
      if (Date.now() - parsed.startedAt > MAX_WATCH_MS) {
        window.sessionStorage.removeItem(POST_MUTATION_WATCH_KEY(tokenId));
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  });

  // Keep sessionStorage in sync with the watcher (so reloads resume polling).
  useEffect(() => {
    if (!tokenId || typeof window === 'undefined') return;
    if (postMutationWatch) {
      window.sessionStorage.setItem(POST_MUTATION_WATCH_KEY(tokenId), JSON.stringify(postMutationWatch));
    } else {
      window.sessionStorage.removeItem(POST_MUTATION_WATCH_KEY(tokenId));
    }
  }, [tokenId, postMutationWatch]);

  useEffect(() => {
    if (!postMutationWatch || !tokenId) return;
    const currentVersion = Number(nftData?.nft?.metadata_version ?? 0);
    if (currentVersion > postMutationWatch.fromVersion) {
      setPostMutationWatch(null);
      return;
    }
    if (Date.now() - postMutationWatch.startedAt > MAX_WATCH_MS) {
      setPostMutationWatch(null);
      return;
    }
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) });
    }, 3_000);
    return () => clearInterval(id);
  }, [postMutationWatch, nftData, tokenId, queryClient]);

  // Post-mint enrichment polling. Two overlapping cases:
  //   1. The nfts row exists but enrichment hasn't fetched metadata yet (metadata_fetched_at
  //      is null) — poll so name/image/traits fill in when ready.
  //   2. Multiple images in metadata.properties.files but some still lack cdn_url — poll
  //      so the carousel fills in slide-by-slide as the media processor caches each to S3.
  // Either case keeps polling; both becoming satisfied stops it. Max 2-minute cap.
  useEffect(() => {
    if (!tokenId) return;
    const nftPoll = nftData?.nft as any;
    if (!nftPoll) return;
    const pendingMetadata =
      nftPoll.processing_status === 'pending' ||
      nftPoll.processing_status === 'metadata_fetched' ||
      nftPoll.processing_status === 'processing_media';
    const files = nftPoll.metadata?.properties?.files;
    const hasUncachedAlternates =
      Array.isArray(files) &&
      files.length > 1 &&
      files.some((f: any) => !f || typeof f.cdn_url !== 'string' || !f.cdn_url);
    if (!pendingMetadata && !hasUncachedAlternates) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 40; // 40 × 3s = 2 minutes
    const id = setInterval(() => {
      attempts++;
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) });
      if (attempts >= MAX_ATTEMPTS) clearInterval(id);
    }, 3_000);
    return () => clearInterval(id);
  }, [tokenId, nftData, queryClient]);

  // Popover state
  const [sellPopoverOpen, setSellPopoverOpen] = useState(false);
  // 'sell' = standard listing, 'auction' = timed auction with reserve.
  const [sellPopoverMode, setSellPopoverMode] = useState<'sell' | 'auction'>('sell');
  const [transferPopoverOpen, setTransferPopoverOpen] = useState(false);
  const [offerPopoverOpen, setOfferPopoverOpen] = useState(false);
  const [offerControlsPopoverOpen, setOfferControlsPopoverOpen] = useState(false);
  const [reportDropdownOpen, setReportDropdownOpen] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);

  // Form state
  const [sellPrice, setSellPrice] = useState('');
  const [sellExpiration, setSellExpiration] = useState('');
  const [sellMarketplace, setSellMarketplace] = useState('xrp.deals');
  const [transferAddress, setTransferAddress] = useState('');
  const [offerAmount, setOfferAmount] = useState('');
  const [offerExpiration, setOfferExpiration] = useState('');
  const [offerMarketplace, setOfferMarketplace] = useState('xrp.deals');

  // Offer controls form state
  const [offersDisabledForm, setOffersDisabledForm] = useState(false);
  const [minOfferPriceForm, setMinOfferPriceForm] = useState('');
  const [updatingOfferControls, setUpdatingOfferControls] = useState(false);

  // Xaman modal state
  const [showXamanModal, setShowXamanModal] = useState(false);
  const [xamanPayload, setXamanPayload] = useState<SigningPayload | null>(null);

  // New transaction flow state
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionStage, setTransactionStage] = useState<TransactionStage>('confirmation');
  const [transactionFees, setTransactionFees] = useState<TransactionFees | null>(null);
  const [transactionNFTDetails, setTransactionNFTDetails] = useState<NFTDetails | null>(null);
  const [txHash, setTxHash] = useState<string | undefined>(undefined);

  // Transaction tracking
  const [currentOperation, setCurrentOperation] = useState<'buy' | 'sell' | 'cancel' | 'cancelBuyOffer' | 'transfer' | 'makeOffer' | 'acceptBuyOffer' | null>(null);
  const [pendingSale, setPendingSale] = useState(false);

  // Promotion dialog state
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);

  // Metadata history modal state (dNFT)
  const [showMetadataHistory, setShowMetadataHistory] = useState(false);
  const [promotionStatus, setPromotionStatus] = useState<PromotionStatus | null>(null);

  // Collection offer state (for "Sell Now" feature)
  const [bestCollectionOffer, setBestCollectionOffer] = useState<CollectionOffer | null>(null);
  const [showAcceptCollectionOfferModal, setShowAcceptCollectionOfferModal] = useState(false);

  // Points distribution — used to decide whether to show the "(X Pts/XRP)" suffix.
  // When points are uniform across the whole collection, the ratio is the same for
  // every listed NFT and just adds visual noise.
  const [pointsDistribution, setPointsDistribution] = useState<PointsDistributionResult | null>(null);
  const hasVariablePoints = !!(pointsDistribution && pointsDistribution.distribution.length > 1);

  // Accept buy offer modal state
  const [showAcceptBuyOfferModal, setShowAcceptBuyOfferModal] = useState(false);
  const [selectedBuyOffer, setSelectedBuyOffer] = useState<FormattedOffer | null>(null);

  // Flip opportunity state (for showing alert when owner may be motivated to sell at a discount)
  const [flipOpportunity, setFlipOpportunity] = useState<WalletFlipOpportunity | null>(null);

  // Trade builder state
  const [showTradeBuilder, setShowTradeBuilder] = useState(false);

  // Custom tag editing state
  const [showTagEditModal, setShowTagEditModal] = useState(false);
  const [editingTags, setEditingTags] = useState<Record<string, string>>({});
  const [savingTags, setSavingTags] = useState(false);

  // Transfer offer acceptance state
  const [acceptingTransfer, setAcceptingTransfer] = useState(false);
  const [transferPayload, setTransferPayload] = useState<SigningPayload | null>(null);
  const [showTransferSigningModal, setShowTransferSigningModal] = useState(false);

  // Cancel transfer offer state
  const [cancellingTransfer, setCancellingTransfer] = useState(false);
  const [cancelTransferPayload, setCancelTransferPayload] = useState<SigningPayload | null>(null);
  const [showCancelTransferModal, setShowCancelTransferModal] = useState(false);

  // Ambassador lock check - only for owner
  const [ambassadorLock, setAmbassadorLock] = useState<{
    lockExpiresAt: string;
    expired: boolean;
  } | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !nft?.owner_address || user?.xrpl_address !== nft.owner_address || !nft.nft_token_id) return;
    // Check if this NFT has an active ambassador lock
    apiClient.get<{ success: boolean; data: { allocations: any[]; total: number } }>(
      `/ambassadors/my-allocations?page=1&limit=100`
    ).then(res => {
      if (res?.success) {
        const alloc = res.data?.allocations?.find(
          (a: any) => a.nft_token_id === nft.nft_token_id && a.status === 'claimed'
        );
        if (alloc?.lock_expires_at) {
          setAmbassadorLock({
            lockExpiresAt: alloc.lock_expires_at,
            expired: new Date(alloc.lock_expires_at) < new Date(),
          });
        }
      }
    }).catch(() => {});
  }, [isAuthenticated, user?.xrpl_address, nft?.owner_address, nft?.nft_token_id]);

  const isOwner = isAuthenticated && user?.xrpl_address === nft?.owner_address;
  const auctionState = useActiveAuction(nft?.nft_token_id);
  // Only ACTIVE auctions block the standard offer UI. Terminal auctions
  // (settled / expired_no_reserve / settlement_stuck) get a small banner
  // via ActiveAuctionView but the standard sections render alongside —
  // settled NFTs have a new owner so the new owner's UI is correct;
  // expired/stuck auctions still have the original owner so they may
  // want to relist immediately.
  const hasActiveAuction = auctionState.auction?.status === 'active';
  const activeSellOffer = isAuthenticated && user?.xrpl_address
    ? sellOffers.find(
        offer => offer.creator === user.xrpl_address
      )
    : undefined;
  const userBuyOffers = isAuthenticated && user?.xrpl_address
    ? buyOffers.filter(offer => offer.creator === user.xrpl_address)
    : [];

  // Check if any queries are refetching after a transaction
  const isRefetchingAfterTransaction = (isFetchingNFT || isFetchingOffers || isFetchingHistory) && !loading && !loadingOffers && !loadingHistory;

  // Debug logging
  useEffect(() => {
    console.log('[NFTDetailPage] State:', {
      isAuthenticated,
      userAddress: user?.xrpl_address,
      nftOwner: nft?.owner_address,
      isOwner,
      currentSellPrice,
      activeSellOffer: activeSellOffer?.offerIndex,
      userBuyOffersCount: userBuyOffers.length,
      nftImageUrl: nft?.image_url,
      nftThumbnailUrl: nft?.thumbnail_url,
      nftName: nft?.name,
      hasNft: !!nft
    });
  }, [isAuthenticated, user, nft, isOwner, currentSellPrice, activeSellOffer, userBuyOffers]);

  // Update current sell price from offers (any type - P2P or marketplace-brokered)
  useEffect(() => {
    const lowestOffer = getLowestSellOffer(sellOffers);
    setCurrentSellPrice(lowestOffer ? {
      amount: lowestOffer.amount,
      offerIndex: lowestOffer.offerIndex,
      expiresAt: lowestOffer.expiresAt,
      destination: lowestOffer.destination,
      isMarketplaceBrokered: lowestOffer.isMarketplaceBrokered ?? false
    } : undefined);
  }, [sellOffers]);

  // Contest eligibility: check if any sell offer for this NFT is in the eligible set
  const isContestEligible = activeContestId && eligibleOfferIds.size > 0 && sellOffers.some(
    (o: FormattedOffer) => eligibleOfferIds.has(o.offerIndex)
  );
  const hasActiveContest = !!activeContestId;

  // Set default sell marketplace based on collection's preferred_marketplace
  useEffect(() => {
    const preferredMarketplace = nft?.collection?.preferred_marketplace || 'xrp.deals';
    setSellMarketplace(preferredMarketplace);
  }, [nft?.collection?.preferred_marketplace]);

  // Set default offer marketplace based on owner's account and email status
  // and collection's preferred_marketplace
  useEffect(() => {
    if (ownerHasVerifiedEmail) {
      // Owner has confirmed email → default to Direct P2P (no fees)
      setOfferMarketplace('p2p');
    } else {
      // Owner has no email or no account → default to collection's preferred marketplace
      const preferredMarketplace = nft?.collection?.preferred_marketplace || 'xrp.deals';
      setOfferMarketplace(preferredMarketplace);
    }
  }, [ownerHasVerifiedEmail, nft?.collection?.preferred_marketplace]);

  // Sync offer controls form state with actual offerControls
  useEffect(() => {
    setOffersDisabledForm(offerControls.offersDisabled);
    setMinOfferPriceForm(offerControls.minOfferPrice?.toString() || '');
  }, [offerControls]);

  // Fetch the collection's points distribution so we can suppress the Pts/XRP
  // suffix on collections where every NFT has the same points value.
  useEffect(() => {
    const collectionId = nft?.collection_id;
    if (!collectionId || nft?.collection_points == null) {
      setPointsDistribution(null);
      return;
    }
    let cancelled = false;
    getCollectionPointsDistribution(collectionId)
      .then(data => { if (!cancelled) setPointsDistribution(data); })
      .catch(() => { /* non-critical; just means we won't hide Pts/XRP */ });
    return () => { cancelled = true; };
  }, [nft?.collection_id, nft?.collection_points]);

  // Load best collection offer for "Sell Now" feature
  // Uses the matching endpoint that filters by trait filters
  useEffect(() => {
    const loadBestCollectionOffer = async () => {
      if (!nft?.nft_token_id || !isOwner) {
        setBestCollectionOffer(null);
        return;
      }

      try {
        // Get the best matching offer for this specific NFT
        // This endpoint filters by trait requirements and excludes the current user as buyer
        const matchingOffer = await getBestMatchingOfferForNFT(
          nft.nft_token_id,
          user?.xrpl_address // Exclude offers created by the current user
        );

        setBestCollectionOffer(matchingOffer);
      } catch (err) {
        console.error('Error loading best collection offer:', err);
        setBestCollectionOffer(null);
      }
    };

    loadBestCollectionOffer();
  }, [nft?.nft_token_id, isOwner, user?.xrpl_address]);

  // Load flip opportunity when viewing someone else's NFT
  // Only shows if the current owner has recently sold NFTs in this collection below floor price
  useEffect(() => {
    const loadFlipOpportunity = async () => {
      // Don't fetch flip opportunity if:
      // - No NFT loaded yet
      // - User is viewing their own NFT
      if (!nft?.nft_token_id || isOwner) {
        setFlipOpportunity(null);
        return;
      }

      try {
        const response = await getNFTFlipOpportunity(nft.nft_token_id);
        if (response.success && response.data) {
          setFlipOpportunity(response.data);
        } else {
          setFlipOpportunity(null);
        }
      } catch (err) {
        console.error('Error loading flip opportunity:', err);
        setFlipOpportunity(null);
      }
    };

    loadFlipOpportunity();
  }, [nft?.nft_token_id, isOwner]);

  // Handler for updating offer controls
  const handleUpdateOfferControls = async () => {
    if (!tokenId || !isAuthenticated) return;

    setUpdatingOfferControls(true);
    setActionError(null);

    try {
      const minPrice = minOfferPriceForm ? parseFloat(minOfferPriceForm) : null;

      // Validate min offer price
      if (minPrice !== null && (isNaN(minPrice) || minPrice <= 0)) {
        setActionError('Minimum offer price must be greater than 0');
        setUpdatingOfferControls(false);
        return;
      }

      const response = await apiClient.patch<any>(`/nfts/${tokenId}/offer-controls`, {
        offersDisabled: offersDisabledForm,
        minOfferPrice: minPrice
      });

      if (response.success) {
        showSuccess('Offer settings updated successfully');
        setOfferControlsPopoverOpen(false);
        // Invalidate NFT query to refetch with new offer controls
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) });
      } else {
        setActionError(response.error || 'Failed to update offer settings');
      }
    } catch (error) {
      console.error('Error updating offer controls:', error);
      setActionError('Failed to update offer settings');
    } finally {
      setUpdatingOfferControls(false);
    }
  };

  // Log NFT data when it loads
  useEffect(() => {
    if (nft) {
      console.log('[NFTDetailPage] NFT Data Loaded:', {
        nft_token_id: nft.nft_token_id,
        name: nft.name,
        owner_address: nft.owner_address,
        issuer_address: nft.issuer_address,
        media_url: nft.media_url,
        thumbnail_url: nft.thumbnail_url,
        image_url: nft.image_url,
        selected_image: getImageUrl(nft),
        collection: nft.collection?.name
      });
    }
  }, [nft]);

  // Fetch promotion status when NFT loads
  useEffect(() => {
    const fetchPromotionStatus = async () => {
      if (!tokenId) return;
      try {
        const response = await getPromotionStatus(tokenId);
        if (response.success && response.data) {
          setPromotionStatus(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch promotion status:', err);
      }
    };
    fetchPromotionStatus();
  }, [tokenId]);

  // Connect to SSE for real-time updates
  useEffect(() => {
    if (!tokenId) return;

    const connect = useSSEStore.getState().connect;
    const disconnect = useSSEStore.getState().disconnect;

    // Connect to per-NFT SSE endpoint
    connect(tokenId);

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [tokenId]);

  // Subscribe to SSE events for real-time offer updates
  useEffect(() => {
    if (!tokenId) return;

    const unsubscribeCreated = sseConnection.subscribe('offer_created', (data) => {
      if (data.nftTokenId === tokenId) {
        console.log('Offer created:', data);
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId) });
      }
    });

    const unsubscribeCancelled = sseConnection.subscribe('offer_cancelled', (data) => {
      if (data.nftTokenId === tokenId) {
        console.log('Offer cancelled:', data);
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId) });
      }
    });

    const unsubscribeAccepted = sseConnection.subscribe('offer_accepted', (data) => {
      if (data.nftTokenId === tokenId) {
        console.log('Offer accepted:', data);
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) });
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId) });
        queryClient.invalidateQueries({ queryKey: nftQueryKeys.history(tokenId) });
      }
    });

    return () => {
      unsubscribeCreated();
      unsubscribeCancelled();
      unsubscribeAccepted();
    };
  }, [tokenId, queryClient]);

  // Timeout handling for slow transactions
  useEffect(() => {
    // Only start timeout if queries are refetching after transaction
    if (!isRefetchingAfterTransaction || !currentOperation) return;

    const TRANSACTION_TIMEOUT = 60000; // 60 seconds
    const timeoutId = setTimeout(() => {
      // Check if still fetching after timeout
      if (isFetchingNFT || isFetchingOffers || isFetchingHistory) {
        setActionError('Operation is taking longer than expected. Please refresh the page to see if it completed.');
        showError('Operation is taking longer than expected. Please refresh the page.');
      }
    }, TRANSACTION_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [isRefetchingAfterTransaction, currentOperation, isFetchingNFT, isFetchingOffers, isFetchingHistory, showError]);

  const handleTransactionSigned = (hash?: string) => {
    setProcessing(false);

    if (showTransactionModal && hash) {
      setTxHash(hash);
      // Don't change stage - XamanSigningModal handles success display
    }

    if (showXamanModal) {
      setShowXamanModal(false);
    }

    // Close relevant popover based on operation
    if (currentOperation === 'sell') {
      setSellPopoverOpen(false);
    } else if (currentOperation === 'transfer') {
      setTransferPopoverOpen(false);
    } else if (currentOperation === 'makeOffer') {
      setOfferPopoverOpen(false);
    }

    // Invalidate queries based on operation type
    if (!tokenId) return;

    if (currentOperation === 'buy' || currentOperation === 'transfer' || currentOperation === 'acceptBuyOffer') {
      // These operations change ownership, affects, and history
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) });
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId) });
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.history(tokenId) });
    } else if (currentOperation === 'makeOffer' || currentOperation === 'sell' || currentOperation === 'cancel' || currentOperation === 'cancelBuyOffer') {
      // These operations change offers only (and potentially floor price)
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId) });
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId) }); // NFT data includes min_sell_price
    }

    // Also invalidate collection if this NFT belongs to one (for floor price updates)
    if (nft?.collection_id) {
      queryClient.invalidateQueries({ queryKey: ['collection', nft.collection_id] });
    }

    // Show context-specific success messages
    if (hash) {
      let message = `Transaction successful! Hash: ${hash.substring(0, 8)}...`;

      if (currentOperation === 'buy') {
        const isBrokered = transactionFees?.isMarketplaceBrokered;
        if (isBrokered) {
          message = 'Purchase signed! The NFT will appear in your wallet shortly after processing.';
        } else {
          message = 'Congratulations! You now own this NFT!';
        }
      } else if (currentOperation === 'transfer') {
        message = 'NFT transferred successfully!';
      } else if (currentOperation === 'sell') {
        message = 'Your NFT is now listed for sale!';
      } else if (currentOperation === 'cancel') {
        message = 'Your listing has been cancelled successfully!';
      } else if (currentOperation === 'makeOffer') {
        message = 'Your offer has been submitted successfully!';
      } else if (currentOperation === 'cancelBuyOffer') {
        message = 'Your offer has been cancelled successfully!';
      } else if (currentOperation === 'acceptBuyOffer') {
        message = 'Offer accepted! The NFT has been transferred.';
      }

      showSuccess(message);
    }
  };

  const handleBuyNow = () => {
    // Allow both authenticated users and guests to buy
    if (!currentSellPrice || !nft) return;

    const amount = currentSellPrice.amount / 1_000_000;
    const destination = currentSellPrice.destination;

    if (destination) {
      // Marketplace-brokered: include platform fee
      const platformFee = calculateMarketplaceFee(currentSellPrice.amount, destination) / 1_000_000;
      const totalCost = amount + platformFee;

      setTransactionFees({
        amount,
        platformFee,
        networkFee: 0,
        totalCost,
        isMarketplaceBrokered: true,
        feePercentage: getMarketplaceFeePercentage(destination),
        marketplaceName: getMarketplaceName(destination),
      });
    } else {
      // P2P: no platform fee
      setTransactionFees({
        amount,
        platformFee: 0,
        networkFee: 0,
        totalCost: amount,
        isMarketplaceBrokered: false,
        feePercentage: 0,
        marketplaceName: 'P2P',
      });
    }

    setTransactionNFTDetails({
      name: nft.name || 'Unknown NFT',
      imageUrl: getThumbnailUrl(nft),
      collectionName: nft.collection?.name || 'Unknown Collection',
      tokenId: nft.nft_token_id
    });

    setCurrentOperation('buy');
    setTransactionStage('confirmation');
    setTxHash(undefined);
    setShowTransactionModal(true);
  };

  const handleConfirmTransaction = async () => {
    if (!currentSellPrice || !nft) return;

    setProcessing(true);
    setActionError(null);

    try {
      // Check if user is authenticated
      const isUserAuthenticated = isAuthenticated && user?.xrpl_address;

      if (isUserAuthenticated) {
        // AUTHENTICATED FLOW - use existing endpoints with user token for push notifications
        if (currentSellPrice.destination) {
          // Marketplace-brokered: create matching buy offer
          const buyOfferAmount = calculateBuyAmount(currentSellPrice.amount, currentSellPrice.destination);
          const response = await apiClient.post<any>('/offers/create', {
            nftTokenId: nft.nft_token_id,
            offerType: 'buy',
            amount: buyOfferAmount,
            owner: nft.owner_address,
            userAddress: user!.xrpl_address,
            destination: currentSellPrice.destination
          });

          if (response.success && (response.data?.signingIntent || response.data?.payload)) {
            setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
            setTransactionStage('signing');
          } else {
            setActionError(response.error || 'Failed to create buy offer');
            setTransactionStage('error');
          }
        } else {
          // P2P: directly accept the sell offer
          const response = await apiClient.post<any>('/offers/accept-p2p', {
            nftTokenId: nft.nft_token_id,
            offerIndex: currentSellPrice.offerIndex,
            userAddress: user!.xrpl_address,
            offerType: 'sell'
          });

          if (response.success && (response.data?.signingIntent || response.data?.payload)) {
            setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
            setTransactionStage('signing');
          } else {
            setActionError(response.error || 'Failed to accept P2P offer');
            setTransactionStage('error');
          }
        }
      } else {
        // GUEST FLOW - backend determines cafe vs P2P automatically
        const response = await apiClient.post<any>('/offers/guest/purchase', {
          nftTokenId: nft.nft_token_id,
          offerIndex: currentSellPrice.offerIndex,
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
          setTransactionStage('signing');
        } else {
          setActionError(response.error || 'Failed to create purchase request');
          setTransactionStage('error');
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to process transaction';
      setActionError(errorMsg);
      setTransactionStage('error');
    } finally {
      setProcessing(false);
    }
  };

  const handleSell = async () => {
    if (!isAuthenticated || !isOwner) {
      setActionError('You must be the owner to list this NFT for sale');
      return;
    }

    if (!sellPrice) {
      setActionError('Please enter a sale price');
      return;
    }

    if (!user?.xrpl_address || !nft) {
      setActionError('User address not available');
      return;
    }

    const priceDrops = Math.floor(parseFloat(sellPrice) * 1_000_000);
    if (priceDrops < 1) {
      setActionError('Price must be at least 0.000001 XRP');
      return;
    }

    setProcessing(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/create', {
        nftTokenId: nft.nft_token_id,
        offerType: 'sell',
        amount: priceDrops,
        expiresIn: sellExpiration ? parseInt(sellExpiration) * 86400 : undefined,
        userAddress: user.xrpl_address,
        destination: getMarketplaceOption(sellMarketplace)?.destination || undefined
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        console.log('[handleSell] API Response payload:', response.data.signingIntent ?? response.data.payload);
        setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setCurrentOperation('sell');
        setShowXamanModal(true);
        // Clear form fields
        setSellPrice('');
        setSellExpiration('');
        // Reset marketplace to collection's preferred marketplace
        setSellMarketplace(nft?.collection?.preferred_marketplace || 'xrp.deals');
      } else {
        setActionError(response.error || 'Failed to create sell offer');
        setProcessing(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create sell offer');
      setProcessing(false);
    }
  };

  const handleTransfer = async () => {
    if (!isAuthenticated || !isOwner) {
      setActionError('You must be the owner to transfer this NFT');
      return;
    }

    if (!transferAddress) {
      setActionError('Please enter a recipient address');
      return;
    }

    if (!/^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(transferAddress)) {
      setActionError('Invalid XRPL address format');
      return;
    }

    if (!user?.xrpl_address || !nft) {
      setActionError('User address not available');
      return;
    }

    setProcessing(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/create', {
        nftTokenId: nft.nft_token_id,
        offerType: 'sell',
        amount: 0,
        destination: transferAddress,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setCurrentOperation('transfer');
        setShowXamanModal(true);
        // Clear form field
        setTransferAddress('');
      } else {
        setActionError(response.error || 'Failed to create transfer offer');
        setProcessing(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create transfer');
      setProcessing(false);
    }
  };

  const handleMakeOffer = async () => {
    if (!isAuthenticated) {
      setActionError('You must be logged in to make an offer');
      return;
    }

    if (!offerAmount) {
      setActionError('Please enter an offer amount');
      return;
    }

    if (!user?.xrpl_address || !nft) {
      setActionError('User address not available');
      return;
    }

    const amountDrops = Math.floor(parseFloat(offerAmount) * 1_000_000);
    if (amountDrops < 1) {
      setActionError('Offer must be at least 0.000001 XRP');
      return;
    }

    setProcessing(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/create', {
        nftTokenId: nft.nft_token_id,
        offerType: 'buy',
        amount: amountDrops,
        expiresIn: offerExpiration ? parseInt(offerExpiration) * 86400 : undefined,
        userAddress: user.xrpl_address,
        destination: getMarketplaceOption(offerMarketplace)?.destination || undefined
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setCurrentOperation('makeOffer');
        setShowXamanModal(true);
        // Clear form fields
        setOfferAmount('');
        setOfferExpiration('');
        // Reset to default based on owner email status and collection's preferred marketplace
        const preferredMarketplace = nft?.collection?.preferred_marketplace || 'xrp.deals';
        setOfferMarketplace(ownerHasVerifiedEmail ? 'p2p' : preferredMarketplace);
      } else {
        setActionError(response.error || 'Failed to create buy offer');
        setProcessing(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to create offer');
      setProcessing(false);
    }
  };

  const handleCancelListing = async () => {
    if (!isAuthenticated || !isOwner) {
      setActionError('You must be the owner to cancel the listing');
      return;
    }

    if (!activeSellOffer || !user?.xrpl_address || !nft) {
      setActionError('No active listing to cancel');
      return;
    }

    setProcessing(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/cancel', {
        nftTokenId: nft.nft_token_id,
        offerIndex: activeSellOffer.offerIndex,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setCurrentOperation('cancel');
        setShowXamanModal(true);
      } else {
        setActionError(response.error || 'Failed to cancel listing');
        setProcessing(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel listing');
      setProcessing(false);
    }
  };

  const handleCancelBuyOffer = async (offerIndex: string) => {
    if (!isAuthenticated) {
      setActionError('You must be logged in to cancel an offer');
      return;
    }

    // Verify the offer belongs to the user
    const offer = buyOffers.find(o => o.offerIndex === offerIndex);
    if (!offer || offer.creator !== user?.xrpl_address) {
      setActionError('You can only cancel your own offers');
      return;
    }

    if (!user?.xrpl_address || !nft) {
      setActionError('User address not available');
      return;
    }

    setProcessing(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/cancel', {
        nftTokenId: nft.nft_token_id,
        offerIndex: offerIndex,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setXamanPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setCurrentOperation('cancelBuyOffer');
        setShowXamanModal(true);
      } else {
        setActionError(response.error || 'Failed to cancel buy offer');
        setProcessing(false);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel buy offer');
      setProcessing(false);
    }
  };

  const handleAcceptBuyOffer = (offer: FormattedOffer) => {
    if (!isAuthenticated || !isOwner) {
      setActionError('You must be the owner to accept this offer');
      return;
    }
    setSelectedBuyOffer(offer);
    setShowAcceptBuyOfferModal(true);
  };

  // Handler for accepting a pending transfer offer
  const handleAcceptTransfer = async () => {
    if (!isAuthenticated || !user?.xrpl_address || !pendingTransferOffer || !nft) {
      setActionError('Unable to accept transfer');
      return;
    }

    setAcceptingTransfer(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/accept-p2p', {
        nftTokenId: nft.nft_token_id,
        offerIndex: pendingTransferOffer.offerId,
        userAddress: user.xrpl_address,
        offerType: 'sell'
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setTransferPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setShowTransferSigningModal(true);
      } else {
        setActionError(response.error || 'Failed to accept transfer');
      }
    } catch (err) {
      setActionError('Failed to accept transfer');
    } finally {
      setAcceptingTransfer(false);
    }
  };

  const handleTransferSuccess = () => {
    setShowTransferSigningModal(false);
    setTransferPayload(null);
    showSuccess('Transfer accepted successfully! The NFT is now yours.');
    // Invalidate queries to refresh the page
    queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId!) });
    queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId!) });
  };

  const handleTransferRejected = () => {
    setShowTransferSigningModal(false);
    setTransferPayload(null);
    setActionError('Transaction was rejected');
  };

  // Handler for cancelling an outgoing transfer offer
  const handleCancelTransfer = async () => {
    if (!isAuthenticated || !user?.xrpl_address || !outgoingTransferOffer || !nft) {
      setActionError('Unable to cancel transfer');
      return;
    }

    setCancellingTransfer(true);
    setActionError(null);

    try {
      const response = await apiClient.post<any>('/offers/cancel', {
        nftTokenId: nft.nft_token_id,
        offerIndex: outgoingTransferOffer.offerId,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setCancelTransferPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setShowCancelTransferModal(true);
      } else {
        setActionError(response.error || 'Failed to cancel transfer');
      }
    } catch (err) {
      setActionError('Failed to cancel transfer');
    } finally {
      setCancellingTransfer(false);
    }
  };

  const handleCancelTransferSuccess = () => {
    setShowCancelTransferModal(false);
    setCancelTransferPayload(null);
    showSuccess('Transfer offer cancelled successfully.');
    // Delete the transfer_offers row so it doesn't reappear before ingestion processes the on-chain tx
    if (outgoingTransferOffer?.offerId) {
      apiClient.post('/offers/transfer/confirm', { offerId: outgoingTransferOffer.offerId }).catch(() => {});
    }
    queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId!) });
    queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId!) });
  };

  const handleCancelTransferRejected = () => {
    setShowCancelTransferModal(false);
    setCancelTransferPayload(null);
    setActionError('Transaction was rejected');
  };

  const handleTransferExpired = () => {
    setShowTransferSigningModal(false);
    setTransferPayload(null);
    setActionError('Transaction expired. Please try again.');
  };

  const formatXRP = (drops: number): string => {
    return formatPrice(drops / 1_000_000);
  };

  const formatAddress = (address: string): string => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const formatTokenId = (tokenId: string): string => {
    return `${tokenId.slice(0, 8)}...${tokenId.slice(-6)}`;
  };

  const formatPercentageAboveFloor = (percentage: number): string => {
    // If less than 1%, show to nearest tenth (e.g., 0.5%)
    if (Math.abs(percentage) < 1) {
      return percentage.toFixed(1);
    }
    // Otherwise, show as whole number with thousand separators (e.g., 125,528%)
    return Math.round(percentage).toLocaleString();
  };

  const getWalletUrl = (address: string): string => {
    return `/wallets/${address}`;
  };

  const copyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(successMessage);
    } catch (err) {
      showError('Failed to copy to clipboard');
    }
  };

  const handleReport = async (reportType: ReportType) => {
    if (!nft?.nft_token_id) return;

    if (!isAuthenticated) {
      showError('Please connect your wallet to report an issue');
      return;
    }

    setSubmittingReport(true);
    setReportDropdownOpen(false);

    try {
      await submitNFTReport(nft.nft_token_id, reportType);
      showSuccess('Report submitted successfully. Thank you for your feedback!');
    } catch (err) {
      showError('Failed to submit report. Please try again later.');
    } finally {
      setSubmittingReport(false);
    }
  };

  // Handle opening the tag edit modal
  const handleOpenTagEdit = () => {
    // Initialize editingTags with current values
    const initialValues: Record<string, string> = {};
    for (const tag of collectionTags) {
      const existingValue = customTags.find(ct => ct.tag_id === tag.id);
      initialValues[tag.id] = existingValue?.value || '';
    }
    setEditingTags(initialValues);
    setShowTagEditModal(true);
  };

  // Handle saving edited tags
  const handleSaveTags = async () => {
    if (!nft?.nft_token_id || !nft?.collection?.slug) return;

    // Validate URL fields before saving
    for (const tag of collectionTags) {
      const value = editingTags[tag.id]?.trim();
      if (value && tag.tag_type === 'url') {
        try {
          new URL(value); // Throws if invalid URL
        } catch {
          showError(`Invalid URL for "${tag.display_name}". Please enter a valid URL starting with http:// or https://`);
          return;
        }
      }
      // Validate number fields
      if (value && tag.tag_type === 'number') {
        const num = parseFloat(value);
        if (isNaN(num)) {
          showError(`Invalid number for "${tag.display_name}". Please enter a valid number.`);
          return;
        }
      }
    }

    setSavingTags(true);
    try {
      // Only send tags that have values
      const tagsToUpdate = Object.entries(editingTags)
        .filter(([_, value]) => value.trim() !== '')
        .map(([tag_id, value]) => ({ tag_id, value: value.trim() }));

      if (tagsToUpdate.length === 0) {
        setShowTagEditModal(false);
        return;
      }

      if (!nft.collection.project_slug) {
        showError('Cannot edit tags: collection is not linked to a project');
        return;
      }

      await setNftTagValues(nft.collection.project_slug, nft.collection.slug, nft.nft_token_id, tagsToUpdate);
      showSuccess('Tags updated successfully');
      setShowTagEditModal(false);
      // Refetch NFT data to show updated tags
      queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(nft.nft_token_id) });
    } catch (err) {
      console.error('Error saving tags:', err);
      showError('Failed to save tags. Please try again.');
    } finally {
      setSavingTags(false);
    }
  };

  const formatExpiration = (timestamp: number | undefined): string => {
    if (!timestamp) return 'No expiration';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (nft: NFT): string => {
    // If collection uses original media (dynamic NFTs), prefer original URL
    const useOriginal = nft.collection?.use_original_media && nft.image_url;
    const url = useOriginal
      ? nft.image_url
      : (nft.media_url || nft.thumbnail_url || nft.image_url);

    if (!url) return 'https://via.placeholder.com/800?text=NFT+Image';

    // If it's an IPFS URL, convert to HTTP gateway
    if (url.startsWith('ipfs://')) {
      const hash = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${hash}`;
    }

    return url;
  };

  const getThumbnailUrl = (nft: NFT): string => {
    // If collection uses original media (dynamic NFTs), prefer original URL
    const useOriginal = nft.collection?.use_original_media && nft.image_url;
    const url = useOriginal
      ? nft.image_url
      : (nft.thumbnail_url || nft.media_url || nft.image_url);

    if (!url) return 'https://via.placeholder.com/800?text=NFT+Image';

    // If it's an IPFS URL, convert to HTTP gateway
    if (url.startsWith('ipfs://')) {
      const hash = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${hash}`;
    }

    return url;
  };

  const getFullMediaUrl = (nft: NFT): string => {
    // If collection uses original media (dynamic NFTs), prefer original URL
    const useOriginal = nft.collection?.use_original_media && nft.image_url;
    const url = useOriginal
      ? nft.image_url
      : (nft.media_url || nft.image_url);

    if (!url) return 'https://via.placeholder.com/800?text=NFT+Image';

    // If it's an IPFS URL, convert to HTTP gateway
    if (url.startsWith('ipfs://')) {
      const hash = url.replace('ipfs://', '');
      return `https://ipfs.io/ipfs/${hash}`;
    }

    return url;
  };

  /**
   * Collect every media asset for this NFT as typed carousel slides, in display
   * order: primary still → animation_url (the main rich asset) → extra files.
   * Uses metadata.properties.files (the Metaplex convention we adopted for
   * single-NFT mints) as the authoritative list, which carries each entry's mime
   * `type` and a `cdn_url` (cached by our enrichment media processor). Each slide
   * may be an image, a video, or a 3D model. Prefers cdn_url, falling back to
   * ipfs:// → gateway for uncached slides.
   */
  const getMediaSlides = (nft: NFT): { url: string; kind: 'image' | 'video' | '3d' }[] => {
    const classify = (type: string | null | undefined, uri: string): 'image' | 'video' | '3d' => {
      const t = (type || '').toLowerCase();
      if (t.startsWith('video/')) return 'video';
      if (t === 'model/gltf-binary') return '3d';
      const path = uri.split('?')[0].split('#')[0].toLowerCase();
      if (/\.(mp4|webm|ogv)$/.test(path)) return 'video';
      if (path.endsWith('.glb')) return '3d';
      return 'image';
    };

    const toHttp = (uri: string) =>
      uri.startsWith('ipfs://') ? `https://ipfs.io/ipfs/${uri.slice('ipfs://'.length)}` : uri;

    const primaryRawImage = ((nft.metadata as any)?.image as string | undefined) || '';
    const primaryDisplayUrl = getFullMediaUrl(nft);

    // Metaplex-style metadata (common from xrp.cafe / NFTexport.io mints) uses
    // relative URIs in `properties.files` rooted at the metadata JSON's IPFS
    // directory. We don't have the metadata dir handy here, but the primary
    // `image` is typically in the same directory, so resolving relative refs
    // against it catches the "files[0] is really just the primary by basename"
    // case. Mirrors the logic in the enrichment media processor.
    const primaryIpfsBase = (() => {
      if (!primaryRawImage.startsWith('ipfs://')) return null;
      const lastSlash = primaryRawImage.lastIndexOf('/');
      if (lastSlash <= 'ipfs://'.length) return null;
      return primaryRawImage.slice(0, lastSlash + 1);
    })();
    const resolveRelative = (raw: string): string => {
      if (/^(ipfs|https?|ar):\/\//i.test(raw)) return raw;
      if (raw.includes(':')) return raw;
      if (!primaryIpfsBase) return raw;
      const cleaned = raw.replace(/^\.\//, '');
      if (cleaned.startsWith('/') || cleaned.includes('..')) return raw;
      return primaryIpfsBase + cleaned;
    };

    // Per Metaplex spec, `properties.files` lists additional files beyond the
    // top-level `image`. So we always prepend the primary, then append each
    // entry from `properties.files`. Entries whose URI resolves to the primary
    // (both legacy absolute duplicates and Metaplex-style relative refs) get
    // filtered by the skip below.
    const slides: { url: string; kind: 'image' | 'video' | '3d' }[] = [];
    const seen = new Set<string>();
    const markSeen = (...keys: (string | null | undefined)[]) => {
      for (const k of keys) if (k) seen.add(k);
    };

    if (primaryDisplayUrl) {
      // The primary is the still preview; classify by media_type for safety so
      // externally-minted NFTs whose primary is itself a video/3D still render right.
      slides.push({ url: primaryDisplayUrl, kind: classify(nft.media_type, primaryRawImage) });
      markSeen(primaryDisplayUrl, primaryRawImage);
    }

    const files = (nft.metadata as any)?.properties?.files;
    if (Array.isArray(files)) {
      for (const f of files) {
        if (!f || typeof f !== 'object') continue;
        const uri = typeof f.uri === 'string' ? f.uri : '';
        if (!uri) continue;
        const resolvedUri = resolveRelative(uri);
        if (resolvedUri === primaryRawImage) continue; // same file as primary
        const type = typeof f.type === 'string' ? f.type : '';
        const cdn = typeof f.cdn_url === 'string' ? f.cdn_url : '';
        const display = cdn || toHttp(resolvedUri);
        if (seen.has(display) || seen.has(resolvedUri) || seen.has(uri)) continue;
        slides.push({ url: display, kind: classify(type, uri) });
        markSeen(display, resolvedUri, uri);
      }
    }

    // Fallback: a top-level animation_url not already represented in properties.files
    // (covers externally-minted NFTs that don't enumerate it). Our own mints list the
    // animation asset in files, so this is deduped away for them.
    const animRaw =
      ((nft.metadata as any)?.animation_url as string | undefined) || nft.animation_url || '';
    if (animRaw && !seen.has(animRaw)) {
      const animDisplay = toHttp(animRaw);
      if (!seen.has(animDisplay)) {
        let kind = classify(null, animRaw);
        if (kind === 'image') {
          // No extension/type hint — lean on media_type, else assume video.
          if (nft.media_type === 'model/gltf-binary') kind = '3d';
          else kind = 'video';
        }
        slides.push({ url: animDisplay, kind });
        markSeen(animDisplay, animRaw);
      }
    }

    return slides.length > 0 ? slides : [{ url: primaryDisplayUrl, kind: 'image' as const }];
  };

  const getVideoUrl = (nft: NFT): string | null => {
    if (nft.media_type?.startsWith('video/')) {
      return nft.animation_url || nft.media_url || null;
    }
    // Prefer centralisedUri (.mp4) for reliable playback, fall back to IPFS video
    const candidates = [
      nft.metadata?.altImageData?.centralisedUri,
      nft.metadata?.altImageData?.rawCentralisedUri,
      nft.animation_url,
      nft.metadata?.video,
      nft.metadata?.animation_url,
    ].filter(Boolean) as string[];
    for (const url of candidates) {
      const path = url.split('?')[0].split('#')[0].toLowerCase();
      if (path.endsWith('.mp4') || path.endsWith('.webm') || path.endsWith('.ogv')) {
        return url;
      }
    }
    // If metadata has an explicit "video" field, trust it even without extension
    if (nft.metadata?.video) return nft.metadata.video;
    return null;
  };


  const getTransactionIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'mint':
      case 'nftokenmint':
        return <FeatherSparkles className="text-body font-body text-default-font" />;
      case 'sale':
      case 'auction_sale':
      case 'nftokenacceptoffer':
        return <FeatherShoppingBag className="text-body font-body text-default-font" />;
      case 'transfer':
        return <FeatherArrowLeftRight className="text-body font-body text-default-font" />;
      case 'list':
      case 'nftokencreateoffer':
        return <FeatherTag className="text-body font-body text-default-font" />;
      default:
        return <FeatherTag className="text-body font-body text-default-font" />;
    }
  };

  const getModalTitle = (): string => {
    switch (currentOperation) {
      case 'makeOffer':
        return 'Submit Offer';
      case 'sell':
        return 'List for Sale';
      case 'transfer':
        return 'Transfer NFT';
      case 'cancel':
        return 'Cancel Listing';
      case 'cancelBuyOffer':
        return 'Cancel Offer';
      case 'acceptBuyOffer':
        return 'Accept Offer';
      default:
        return 'Sign Transaction';
    }
  };

  const getModalMessage = (): string => {
    switch (currentOperation) {
      case 'makeOffer':
        return 'Scan the QR code with Xaman and sign to submit offer';
      case 'sell':
        return 'Scan the QR code with Xaman and sign to list for sale';
      case 'transfer':
        return 'Scan the QR code with Xaman and sign to transfer';
      case 'cancel':
        return 'Scan the QR code with Xaman and sign to cancel listing';
      case 'cancelBuyOffer':
        return 'Scan the QR code with Xaman and sign to cancel offer';
      case 'acceptBuyOffer':
        return 'Scan the QR code with Xaman and sign to accept this offer';
      default:
        return 'Scan the QR code with Xaman and sign to complete transaction';
    }
  };

  const getTransactionLabel = (type: string): string => {
    switch (type.toLowerCase()) {
      case 'mint':
      case 'nftokenmint':
        return 'Mint';
      case 'auction_sale':
        return 'Auction sale';
      case 'sale':
      case 'nftokenacceptoffer':
        return 'Sale';
      case 'transfer':
        return 'Transfer';
      case 'list':
      case 'nftokencreateoffer':
        return 'List';
      default:
        return type;
    }
  };

  const formatHoldTime = (seconds: number | null | undefined): string | null => {
    if (seconds === null || seconds === undefined) return null;

    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}yr`;
    if (months > 0) return `${months}mo`;
    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    return '<1h';
  };

  const formatProfitLoss = (profitLossXrp: number | null | undefined, roiPercentage: number | null | undefined) => {
    if (profitLossXrp === null || profitLossXrp === undefined) {
      return { display: '–', color: 'text-subtext-color' };
    }

    const isProfit = profitLossXrp > 0;
    const isLoss = profitLossXrp < 0;
    const sign = isProfit ? '+' : isLoss ? '-' : '';
    const color = isProfit ? 'text-success-600' : isLoss ? 'text-error-600' : 'text-subtext-color';

    // Use shared formatNumber utility
    const formattedValue = formatNumber(Math.abs(profitLossXrp));

    const roiSign = roiPercentage !== null && roiPercentage !== undefined
      ? (roiPercentage > 0 ? '+' : roiPercentage < 0 ? '-' : '')
      : '';

    return {
      amount: `${sign}${formattedValue} XRP`,
      percentage: roiPercentage !== null && roiPercentage !== undefined ? `${roiSign}${Math.abs(roiPercentage).toFixed(1)}%` : '',
      color
    };
  };

  const formatSellerMetrics = (
    holdTimeSeconds: number | null | undefined,
    profitLossXrp: number | null | undefined,
    roiPercentage: number | null | undefined
  ): { holdText: string; plText: string; plColor: string } | null => {
    const holdTime = formatHoldTime(holdTimeSeconds);

    // No hold time data at all
    if (!holdTime) {
      return null;
    }

    // Have hold time but no profit/loss data
    if (profitLossXrp === null || profitLossXrp === undefined) {
      return {
        holdText: `Held ${holdTime},`,
        plText: 'P/L unknown',
        plColor: 'text-subtext-color'
      };
    }

    // Have both hold time and profit/loss
    const isProfit = profitLossXrp > 0;
    const isLoss = profitLossXrp < 0;
    const sign = isProfit ? '+' : isLoss ? '-' : '';
    const plColor = isProfit ? 'text-success-600' : isLoss ? 'text-error-600' : 'text-subtext-color';
    const formattedValue = formatNumber(Math.abs(profitLossXrp));
    const verb = isProfit ? 'profited' : isLoss ? 'lost' : '';

    // Format ROI percentage if available
    let roiText = '';
    if (roiPercentage !== null && roiPercentage !== undefined) {
      const roiSign = roiPercentage > 0 ? '+' : roiPercentage < 0 ? '-' : '';
      roiText = ` (${roiSign}${Math.abs(roiPercentage).toFixed(1)}%)`;
    }

    return {
      holdText: `Held ${holdTime},`,
      plText: `${verb} ${sign}${formattedValue} XRP${roiText}`,
      plColor
    };
  };

  if (loading) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-12">
        <div className="text-heading-2 font-heading-2 text-default-font">Loading NFT...</div>
      </div>
    );
  }

  if (error || !nft) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-12">
        <div className="text-heading-2 font-heading-2 text-error-600 mb-4">Error</div>
        <div className="text-body font-body text-subtext-color mb-4">{error || 'NFT not found'}</div>
        <Button onClick={() => navigate('/collections')}>Back to Collections</Button>
      </div>
    );
  }

  return (
    <>
      <DefaultPageLayout>
        <div className="flex w-full max-w-full flex-col items-start gap-6 pb-12 overflow-x-hidden">
        {/* Error/Success Messages */}
        {actionError && (
          <div className="w-full rounded-md border border-solid border-warning-600 bg-warning-100 px-4 py-3 flex items-center justify-between">
            <span className="text-body font-body text-warning-800">{actionError}</span>
            <IconButton
              size="small"
              variant="neutral-tertiary"
              icon={<span>×</span>}
              onClick={() => setActionError(null)}
            />
          </div>
        )}

        {(pendingSale || (isRefetchingAfterTransaction && currentOperation)) && (
          <div className="w-full rounded-md border-2 border-solid border-warning-600 bg-warning-100 px-6 py-4 flex items-center gap-4">
            <div className="text-heading-2 font-heading-2">⏳</div>
            <div className="flex flex-col gap-1">
              <h4 className="text-heading-3 font-heading-3 text-warning-800">Processing Transaction</h4>
              <p className="text-body font-body text-warning-700">Your transaction has been submitted. Waiting for confirmation...</p>
              <p className="text-caption font-caption text-warning-600">Usually completes in 5-30 seconds</p>
            </div>
          </div>
        )}

        {/* Flip Opportunity Alert - shows when owner has recently sold below floor */}
        {flipOpportunity && flipOpportunity.suggested_offer_low && flipOpportunity.suggested_offer_high && (
          <div className="w-full p-4 rounded-lg border border-success-200 bg-success-50">
            <div className="flex items-start gap-3">
              <SubframeCore.FeatherRefreshCw className="w-5 h-5 text-success-600 flex-shrink-0 mt-0.5" />
              <div className="flex flex-col gap-1">
                <span className="text-body-bold font-body-bold text-success-700">
                  Potential Flip Opportunity
                </span>
                <span className="text-body font-body text-success-700">
                  The owner of this NFT recently sold another NFT in this collection (
                  <a
                    href={`/nfts/${flipOpportunity.nft_id}`}
                    className="underline hover:text-success-800"
                  >
                    {flipOpportunity.nft_name ? `"${flipOpportunity.nft_name}"` : 'link'}
                  </a>
                  ) for{' '}
                  <strong>{flipOpportunity.discount_percentage.toFixed(0)}% below floor</strong> ({flipOpportunity.sale_price.toLocaleString()} XRP when floor was {flipOpportunity.floor_price_at_sale.toLocaleString()} XRP).
                  They may be motivated to sell their remaining NFTs at a discount.
                </span>
                <span className="text-body-bold font-body-bold text-success-700 mt-2">
                  Suggested offer: {flipOpportunity.suggested_offer_low === flipOpportunity.suggested_offer_high
                    ? `${flipOpportunity.suggested_offer_low.toLocaleString()} XRP`
                    : `${flipOpportunity.suggested_offer_low.toLocaleString()} – ${flipOpportunity.suggested_offer_high.toLocaleString()} XRP`}
                </span>
                {flipOpportunity.current_floor_price && (
                  <span className="text-caption font-caption text-success-600">
                    Current floor: {flipOpportunity.current_floor_price.toLocaleString()} XRP
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Creator Banner - above main content for managers/issuers with any creator action */}
        {isAuthenticated && (() => {
          const isIssuer = user?.xrpl_address === nft.issuer_address;
          if (nft.is_burned) return null;
          const canMutate = !!nft.is_mutable && (canManageCollection || isIssuer);
          const canEditTags = canManageCollection && collectionTags.length > 0;
          if (!canMutate && !canEditTags) return null;
          return (
            <CreatorToolsSection
              nft={nft}
              canMutate={canMutate}
              canEditTags={canEditTags}
              hasCustomTags={customTags.length > 0}
              isIssuer={isIssuer}
              onEditTags={handleOpenTagEdit}
              onMutationSuccess={(result: MutationResult) => {
                if (result.transactionHash) {
                  showSuccess(`NFT mutated successfully! Tx: ${result.transactionHash.slice(0, 8)}...`);
                } else {
                  showSuccess('NFT mutation queued! It will be processed shortly.');
                }
                queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId!) });
                // Start a version watcher — the ingestion → enrichment cycle takes
                // ~30–90s, and the nft row reads `completed` in the meantime, so
                // the normal status-based poller doesn't kick in.
                const fromVersion = Number((nft as any)?.metadata_version ?? 1);
                setPostMutationWatch({ fromVersion, startedAt: Date.now() });
              }}
              onAuthorizationRequired={(requiredMinter: string) => {
                showError(`NFTokenMinter authorization required. Set ${requiredMinter.slice(0, 8)}... as your NFTokenMinter via an AccountSet transaction.`);
              }}
            />
          );
        })()}

        {/* Main Content */}
        <div className="flex flex-col sm:flex-row w-full items-start gap-6 sm:gap-12">
          {/* Left Column - Image */}
          <div className="flex w-full sm:min-w-[320px] sm:grow sm:shrink-0 sm:basis-0 flex-col items-start gap-4">
            {postMutationWatch && (
              <div className="flex w-full items-center gap-3 rounded-md border border-brand-200 bg-brand-50 px-3 py-2">
                <LoadingSpinner size="sm" className="text-brand-600" />
                <span className="text-caption font-caption text-brand-700">
                  Applying mutation — new images and metadata will appear in a minute or so.
                </span>
              </div>
            )}
            <div className={`flex w-full flex-col items-center rounded-md border border-solid bg-default-background shadow-sm relative overflow-hidden ${
              promotionStatus?.isPromoted ? 'border-amber-400 ring-2 ring-amber-200' : 'border-neutral-border'
            }`}>
              {(nft.processing_status === 'pending' ||
                nft.processing_status === 'metadata_fetched' ||
                nft.processing_status === 'processing_media') ? (
                // Post-mint: ingestion has the NFT row but enrichment hasn't finished
                // caching media (processing_status transitions: pending → metadata_fetched →
                // processing_media → completed). Keep the loading state up until 'completed'
                // so we don't briefly flash a broken image after metadata lands but before
                // the CDN URL is written. The auto-poll above refetches every 3s.
                <div className="w-full aspect-square flex flex-col items-center justify-center gap-4 py-12 text-center">
                  <LoadingSpinner size="xl" className="text-brand-600" />
                  <div>
                    <p className="text-heading-3 font-heading-3 text-default-font">Processing your NFT</p>
                    <p className="text-body font-body text-subtext-color mt-1 max-w-xs">
                      Pinning metadata and media to IPFS. This usually takes 30–60 seconds.
                    </p>
                  </div>
                </div>
              ) : getMediaSlides(nft).length <= 1 && is3DModel(nft) ? (
                <div className="w-full flex justify-center relative group">
                  <model-viewer
                    src={getModelViewerUrl(getFullMediaUrl(nft))}
                    alt={nft.name}
                    auto-rotate=""
                    camera-controls=""
                    shadow-intensity="1"
                    style={{ width: '100%', height: '60vh', maxHeight: '600px', touchAction: 'pan-y' }}
                  />
                  {/* Interaction hint - fades on hover/interact */}
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5 opacity-100 group-hover:opacity-0 transition-opacity duration-500">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9l2-2 2 2"/><path d="M11 9l2-2 2 2"/><path d="M5 15l2 2 2-2"/><path d="M11 15l2 2 2-2"/><circle cx="12" cy="12" r="1"/></svg>
                    Click &amp; drag to interact
                  </div>
                  {/* 3D badge */}
                  <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                    <span style={{ fontSize: '12px' }}>🧊</span> 3D Model
                  </div>
                </div>
              ) : getMediaSlides(nft).length <= 1 && getVideoUrl(nft) ? (
                <video
                  src={getVideoUrl(nft)!}
                  poster={getFullMediaUrl(nft)}
                  controls
                  loop
                  muted
                  autoPlay
                  playsInline
                  className="w-auto max-w-full h-auto max-h-[60vh] sm:max-h-[600px] object-contain rounded-md"
                />
              ) : (() => {
                const slides = getMediaSlides(nft);
                const idx = slides.length > 0 ? Math.min(currentImageIndex, slides.length - 1) : 0;
                const current = slides[idx] || { url: getFullMediaUrl(nft), kind: 'image' as const };
                const hasMultiple = slides.length > 1;

                // Progress indicator: count how many entries in metadata.properties.files
                // still lack a cdn_url (meaning the enrichment media processor hasn't
                // cached them to S3 yet). While any are pending, show a small "still
                // processing" hint so the user doesn't think the media failed. The
                // primary's CDN status is surfaced separately via processing_status.
                const rawFiles = (nft.metadata as any)?.properties?.files;
                let pendingSlides = 0;
                let totalFileSlides = 0;
                if (Array.isArray(rawFiles)) {
                  for (const f of rawFiles) {
                    if (!f || typeof f !== 'object') continue;
                    const type = typeof f.type === 'string' ? f.type : '';
                    const uri = typeof f.uri === 'string' ? f.uri : '';
                    if (!uri) continue;
                    const isCacheable =
                      type.startsWith('image/') ||
                      type.startsWith('video/') ||
                      type === 'model/gltf-binary' ||
                      /\.(png|jpe?g|gif|webp|avif|bmp|mp4|webm|glb)(\?|#|$)/i.test(uri);
                    if (!isCacheable) continue;
                    totalFileSlides++;
                    if (typeof f.cdn_url !== 'string' || !f.cdn_url) pendingSlides++;
                  }
                }
                const showProgress = totalFileSlides > 0 && pendingSlides > 0;

                return (
                  <div className="w-full flex flex-col items-center">
                    {/* Fixed-aspect frame keyed to the still image's ratio (default
                        square until measured). Every slide — image, video, 3D —
                        letterboxes inside it via object-contain, so the container
                        never resizes when you cycle through the carousel. */}
                    <div
                      className="relative w-full flex items-center justify-center overflow-hidden rounded-md max-h-[60vh] sm:max-h-[600px]"
                      style={{ aspectRatio: carouselAspectRatio ?? 1 }}
                    >
                      {current.kind === 'video' ? (
                        <video
                          src={current.url}
                          poster={getFullMediaUrl(nft)}
                          controls
                          loop
                          muted
                          autoPlay
                          playsInline
                          className="w-full h-full object-contain"
                        />
                      ) : current.kind === '3d' ? (
                        <div className="w-full h-full flex justify-center relative group">
                          <model-viewer
                            src={getModelViewerUrl(current.url)}
                            alt={nft.name}
                            auto-rotate=""
                            camera-controls=""
                            shadow-intensity="1"
                            style={{ width: '100%', height: '100%', touchAction: 'pan-y' }}
                          />
                          <div className="absolute top-3 left-3 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded flex items-center gap-1">
                            <span style={{ fontSize: '12px' }}>🧊</span> 3D Model
                          </div>
                        </div>
                      ) : (
                        <a
                          href={current.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full cursor-pointer flex items-center justify-center"
                        >
                          <img
                            className={`max-w-full max-h-full w-auto h-auto object-contain rounded-md${nft.is_burned ? ' saturate-[0.4] brightness-[0.8]' : ''}`}
                            src={current.url}
                            alt={nft.name}
                            onLoad={(e) => {
                              if (idx === 0 && carouselAspectRatio === null) {
                                const w = e.currentTarget.naturalWidth;
                                const h = e.currentTarget.naturalHeight;
                                if (w > 0 && h > 0) setCarouselAspectRatio(w / h);
                              }
                            }}
                            onError={(e) => {
                              console.error('[NFTDetailPage] Image load error:', {
                                media_url: nft.media_url,
                                thumbnail_url: nft.thumbnail_url,
                                image_url: nft.image_url,
                                current: current.url,
                              });
                              e.currentTarget.src = 'https://via.placeholder.com/800?text=NFT+Image';
                            }}
                          />
                        </a>
                      )}
                    </div>
                    {hasMultiple && (
                      <div className="flex flex-col items-center gap-1 py-3 w-full border-t border-neutral-border">
                        <div className="flex items-center justify-center gap-4">
                          <IconButton
                            icon={<FeatherChevronLeft />}
                            variant="neutral-tertiary"
                            aria-label="Previous media"
                            onClick={() =>
                              setCurrentImageIndex((i) => (i - 1 + slides.length) % slides.length)
                            }
                          />
                          <span className="text-caption font-caption text-subtext-color min-w-[3rem] text-center">
                            {idx + 1} / {slides.length}
                          </span>
                          <IconButton
                            icon={<FeatherChevronRight />}
                            variant="neutral-tertiary"
                            aria-label="Next media"
                            onClick={() =>
                              setCurrentImageIndex((i) => (i + 1) % slides.length)
                            }
                          />
                        </div>
                        {showProgress && (
                          <div className="flex items-center gap-2 text-caption font-caption text-subtext-color">
                            <Loader size="small" />
                            <span>
                              Caching additional media…
                              {' '}
                              {totalFileSlides - pendingSlides} of {totalFileSlides} ready
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Burn overlay effect */}
              {nft.is_burned && (
                <>
                  <style>{`
                    @keyframes burn-flicker-1 {
                      0%, 100% { opacity: 0.15; transform: scaleY(1) scaleX(1); }
                      25% { opacity: 0.4; transform: scaleY(1.1) scaleX(0.95); }
                      50% { opacity: 0.2; transform: scaleY(0.95) scaleX(1.05); }
                      75% { opacity: 0.35; transform: scaleY(1.05) scaleX(0.98); }
                    }
                    @keyframes burn-flicker-2 {
                      0%, 100% { opacity: 0.2; transform: scaleY(0.95) scaleX(1.02); }
                      30% { opacity: 0.45; transform: scaleY(1.08) scaleX(0.96); }
                      60% { opacity: 0.15; transform: scaleY(1) scaleX(1.04); }
                      85% { opacity: 0.38; transform: scaleY(1.12) scaleX(0.97); }
                    }
                    @keyframes burn-flicker-3 {
                      0%, 100% { opacity: 0.1; transform: scaleY(1.05) scaleX(0.98); }
                      20% { opacity: 0.3; transform: scaleY(0.92) scaleX(1.03); }
                      45% { opacity: 0.42; transform: scaleY(1.1) scaleX(0.95); }
                      70% { opacity: 0.18; transform: scaleY(0.98) scaleX(1.01); }
                    }
                    @keyframes edge-glow {
                      0%, 100% { opacity: 0.4; }
                      50% { opacity: 0.7; }
                    }
                  `}</style>
                  {/* Dark tint */}
                  <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(0, 0, 0, 0.2)', mixBlendMode: 'multiply', zIndex: 2 }} />
                  {/* Base fire gradient */}
                  <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(220,38,38,0.5) 0%, rgba(234,88,12,0.35) 20%, rgba(251,146,60,0.15) 40%, rgba(251,146,60,0.05) 55%, transparent 70%)', zIndex: 3 }} />
                  {/* Flickering glow patches — soft radial gradients that pulse independently */}
                  <div className="absolute pointer-events-none" style={{
                    bottom: '-5%', left: '0%', width: '50%', height: '70%',
                    background: 'radial-gradient(ellipse at center bottom, rgba(239,68,68,0.5) 0%, rgba(234,88,12,0.3) 30%, transparent 70%)',
                    zIndex: 4, transformOrigin: 'center bottom',
                    animation: 'burn-flicker-1 3s ease-in-out infinite',
                  }} />
                  <div className="absolute pointer-events-none" style={{
                    bottom: '-8%', left: '25%', width: '55%', height: '75%',
                    background: 'radial-gradient(ellipse at center bottom, rgba(251,146,60,0.45) 0%, rgba(234,88,12,0.25) 35%, transparent 70%)',
                    zIndex: 4, transformOrigin: 'center bottom',
                    animation: 'burn-flicker-2 2.5s ease-in-out infinite',
                  }} />
                  <div className="absolute pointer-events-none" style={{
                    bottom: '-3%', left: '50%', width: '50%', height: '65%',
                    background: 'radial-gradient(ellipse at center bottom, rgba(239,68,68,0.4) 0%, rgba(251,146,60,0.2) 30%, transparent 65%)',
                    zIndex: 4, transformOrigin: 'center bottom',
                    animation: 'burn-flicker-3 3.5s ease-in-out infinite',
                  }} />
                  {/* Yellow/white hot core flicker */}
                  <div className="absolute pointer-events-none" style={{
                    bottom: '-2%', left: '20%', width: '60%', height: '40%',
                    background: 'radial-gradient(ellipse at center bottom, rgba(253,224,71,0.3) 0%, rgba(251,146,60,0.15) 40%, transparent 70%)',
                    zIndex: 5, transformOrigin: 'center bottom',
                    animation: 'burn-flicker-1 2s ease-in-out infinite 0.5s',
                  }} />
                  {/* Bottom edge glow line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 pointer-events-none" style={{
                    background: 'linear-gradient(90deg, transparent 5%, #ef4444 20%, #f97316 40%, #fbbf24 50%, #f97316 60%, #ef4444 80%, transparent 95%)',
                    zIndex: 5, animation: 'edge-glow 2s ease-in-out infinite',
                  }} />
                </>
              )}
              {/* Featured Badge */}
              {promotionStatus?.isPromoted && (
                <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-3 py-1.5 shadow-md">
                  <FeatherStar className="h-4 w-4 text-white fill-white" />
                  <span className="text-sm font-semibold text-white">Featured</span>
                </div>
              )}
              {/* Contest Eligible Badge */}
              {hasActiveContest && isContestEligible && (
                <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-brand-600 px-2.5 py-1 shadow-md">
                  <span className="text-xs font-semibold text-white">Contest Eligible</span>
                </div>
              )}
            </div>

            {/* Creator Tags - below image (read-only display; edit button is in the Creator Banner above) */}
            {customTags.length > 0 && (
              <div className="flex w-full flex-col gap-3 rounded-lg border border-neutral-border bg-default-background p-4">
                <div className="flex items-center gap-2">
                  <FeatherTag className="h-4 w-4 text-subtext-color" />
                  <span className="text-body-bold font-body-bold text-default-font">Creator Tags</span>
                  <SubframeCore.Tooltip.Provider delayDuration={0}>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild>
                        <button type="button" className="inline-flex items-center cursor-help">
                          <FeatherInfo className="h-3.5 w-3.5 text-subtext-color" />
                        </button>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content side="bottom" align="start" sideOffset={4} asChild>
                          <Tooltip className="max-w-[240px] whitespace-normal">
                            These tags are custom metadata defined and managed by the collection creator. They are not part of the on-chain NFT data.
                          </Tooltip>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                </div>
                <div className="flex flex-col gap-2">
                  {customTags.map((tag) => (
                    <div key={tag.id} className="flex items-center justify-between gap-2">
                      <span className="text-caption font-caption text-subtext-color">{tag.display_name}</span>
                      <div className="text-caption font-caption text-default-font text-right">
                        {tag.tag_type === 'url' ? (
                          <a
                            href={tag.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-700 hover:text-brand-600 underline"
                          >
                            {tag.value.length > 30 ? `${tag.value.slice(0, 30)}...` : tag.value}
                          </a>
                        ) : tag.tag_type === 'boolean' ? (
                          <span>{tag.value === 'true' || tag.value === '1' ? 'Yes' : 'No'}</span>
                        ) : (
                          <span>{tag.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Right Column - Details & Actions */}
          <div className="flex w-full sm:min-w-[320px] sm:grow sm:shrink-0 sm:basis-0 flex-col items-start justify-center gap-6 self-stretch">
            {/* Header */}
            <div className="flex w-full flex-col items-start justify-center gap-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  {nft.collection ? (
                    <>
                      {nft.collection.project_slug && nft.collection.project_name && (
                        <>
                          <span className="text-heading-3 font-heading-3 text-subtext-color">Project:</span>
                          <Avatar
                            size="small"
                            image={nft.collection.project_image_url || undefined}
                            square={true}
                          >
                            {nft.collection.project_name.slice(0, 1)}
                          </Avatar>
                          <Link
                            to={`/projects/${nft.collection.project_slug}`}
                            className="text-heading-3 font-heading-3 text-brand-700 hover:text-brand-600 underline"
                          >
                            {nft.collection.project_name}
                          </Link>
                          <span className="text-heading-3 font-heading-3 text-subtext-color">/</span>
                        </>
                      )}
                      <Avatar
                        size="small"
                        image={nft.collection.image_url}
                        square={true}
                      >
                        {nft.collection.name.slice(0, 1)}
                      </Avatar>
                      <Link
                        to={`/collections/${nft.collection.slug || nft.collection.id}`}
                        className="text-heading-3 font-heading-3 text-brand-700 hover:text-brand-600 underline"
                      >
                        {nft.collection.name}
                      </Link>
                    </>
                  ) : (
                    <span className="text-heading-3 font-heading-3 text-default-font">
                      Collection
                    </span>
                  )}
                  {nft.collection?.is_verified && (
                    <FeatherVerified className="text-heading-3 font-heading-3 text-brand-700" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <IconButton
                    icon={<FeatherShare />}
                    onClick={() => {
                      const shareUrl = buildShareableUrl(window.location.href, user?.referral_code);
                      navigator.clipboard.writeText(shareUrl);
                      showSuccess('Link copied to clipboard!');
                    }}
                  />
                  {/* Report Issue Dropdown */}
                  <SubframeCore.DropdownMenu.Root open={reportDropdownOpen} onOpenChange={setReportDropdownOpen}>
                    <SubframeCore.DropdownMenu.Trigger asChild>
                      <IconButton
                        icon={<FeatherFlag />}
                        disabled={submittingReport}
                      />
                    </SubframeCore.DropdownMenu.Trigger>
                    <SubframeCore.DropdownMenu.Portal>
                      <SubframeCore.DropdownMenu.Content
                        side="bottom"
                        align="end"
                        sideOffset={4}
                        asChild
                      >
                        <DropdownMenu>
                          <DropdownMenu.DropdownItem
                            icon={<FeatherUser />}
                            onSelect={() => handleReport('incorrect_owner')}
                          >
                            Report incorrect owner
                          </DropdownMenu.DropdownItem>
                          <DropdownMenu.DropdownItem
                            icon={<FeatherTag />}
                            onSelect={() => handleReport('incorrect_offers')}
                          >
                            Report incorrect offers
                          </DropdownMenu.DropdownItem>
                          <DropdownMenu.DropdownItem
                            icon={<FeatherAward />}
                            onSelect={() => handleReport('incorrect_rarity')}
                          >
                            Report incorrect rarity
                          </DropdownMenu.DropdownItem>
                          <DropdownMenu.DropdownItem
                            icon={<FeatherDollarSign />}
                            onSelect={() => handleReport('inaccurate_estimated_value')}
                          >
                            Report inaccurate estimated value
                          </DropdownMenu.DropdownItem>
                        </DropdownMenu>
                      </SubframeCore.DropdownMenu.Content>
                    </SubframeCore.DropdownMenu.Portal>
                  </SubframeCore.DropdownMenu.Root>
                </div>
              </div>
              <span className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
                {nft.name || `NFT #${nft.nft_token_id.slice(0, 8)}...`}
              </span>
              {nft.description && nft.description.trim().length > 0 && (
                <ExpandableDescription text={nft.description} clampLines={2} />
              )}
              {/* Rarity info - only show if rarity is calculated (not NONE methodology) */}
              {/* Rank must be >= 1 (0 or null means no rarity), score must be > 0 */}
              {nft.rarity_rank && nft.rarity_rank >= 1 && nft.collection?.total_supply ? (
                <div className="flex w-full items-center gap-3">
                  <RarityRank
                    rank={nft.rarity_rank}
                    totalSupply={Number(nft.collection.total_supply)}
                    showTotal
                    className="text-body-bold font-body-bold"
                  />
                </div>
              ) : null}
              {/* Dynamic NFT indicators */}
              {nft.is_mutable && (
                <div className="flex w-full items-center gap-2 flex-wrap">
                  <Badge variant="warning">Dynamic NFT</Badge>
                  {nft.metadata_version && nft.metadata_version > 1 && nft.metadata_updated_at && (
                    <button
                      onClick={() => setShowMetadataHistory(true)}
                      className="text-caption text-subtext-color hover:text-brand-primary hover:underline transition-colors cursor-pointer"
                    >
                      Metadata updated {formatDistanceToNow(new Date(nft.metadata_updated_at), { addSuffix: true })} (v{nft.metadata_version})
                    </button>
                  )}
                </div>
              )}
              {/* Burned indicator */}
              {nft.is_burned && (
                <div className="flex w-full items-center gap-2">
                  <Badge variant="error">Burned</Badge>
                </div>
              )}
              {/* Collection Points - show if points are assigned */}
              {nft.collection_points != null && (() => {
                const points = typeof nft.collection_points === 'string'
                  ? parseFloat(nft.collection_points)
                  : nft.collection_points;
                if (!Number.isFinite(points)) return null;
                const pointsPerXrp = hasVariablePoints && nft.min_sell_price && nft.min_sell_price > 0 && points > 0
                  ? points / nft.min_sell_price
                  : null;
                return (
                  <div className="flex w-full items-center gap-2">
                    <span className="text-body-bold font-body-bold text-default-font">
                      Richlist Points: <span className="text-brand-primary">{formatPoints(points)}</span>
                      {pointsPerXrp != null && (
                        <span className="text-subtext-color font-normal"> ({formatPoints(pointsPerXrp)} Pts/XRP)</span>
                      )}
                    </span>
                  </div>
                );
              })()}
              {/* Estimated Value - show if valuation exists and is valid */}
              {valuation &&
               typeof valuation.valueLow === 'number' &&
               typeof valuation.valueHigh === 'number' &&
               isFinite(valuation.valueLow) &&
               isFinite(valuation.valueHigh) &&
               valuation.valueLow > 0 && (
                <div className="flex w-full items-center gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Estimated Value: {Math.round(valuation.valueLow)} - {Math.round(valuation.valueHigh)} XRP
                  </span>
                </div>
              )}
            </div>

            {/* Price & Actions - Conditional Boxes */}
            <div className="flex w-full flex-col items-start justify-center gap-6">
              {/* Burned NFT - no actions available */}
              {nft.is_burned && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border-2 border-solid border-red-500/50 bg-red-500/10 px-4 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🔥</span>
                    <span className="text-body-bold font-body-bold text-red-400">
                      This NFT Has Been Burned
                    </span>
                  </div>
                  <p className="text-body text-subtext-color">
                    This NFT has been permanently destroyed on the XRP Ledger. It can no longer be traded, transferred, or have offers placed on it.
                  </p>
                </div>
              )}

              {/* Pending Transfer Offer - shown when someone has sent this NFT to the viewer (but not if they already own it) */}
              {!nft.is_burned && pendingTransferOffer && isAuthenticated && !isOwner && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border-2 border-solid border-success-500 bg-success-500/10 px-4 py-4">
                  <div className="flex items-center gap-2">
                    <FeatherArrowLeftRight className="w-5 h-5 text-success-600" />
                    <span className="text-body-bold font-body-bold text-default-font">
                      Transfer Offer
                    </span>
                    <Badge variant="success">Free</Badge>
                  </div>
                  <p className="text-body text-default-font">
                    This NFT is being transferred to you from{' '}
                    <Link to={getWalletUrl(pendingTransferOffer.fromAddress)} className="text-brand-primary hover:underline">
                      {formatAddress(pendingTransferOffer.fromAddress)}
                    </Link>
                  </p>
                  <Button
                    variant="brand-primary"
                    size="large"
                    onClick={handleAcceptTransfer}
                    disabled={acceptingTransfer}
                    loading={acceptingTransfer}
                    className="w-full sm:w-auto"
                  >
                    Accept Transfer
                  </Button>
                </div>
              )}

              {/* Outgoing Transfer Offer - shown when the owner has a pending transfer to someone else */}
              {!nft.is_burned && outgoingTransferOffer && isAuthenticated && isOwner && (
                <div className="flex w-full flex-col items-start gap-3 rounded-lg border-2 border-solid border-warning-500 bg-warning-500/10 px-4 py-4">
                  <div className="flex items-center gap-2">
                    <FeatherArrowLeftRight className="w-5 h-5 text-warning-600" />
                    <span className="text-body-bold font-body-bold text-default-font">
                      Pending Transfer
                    </span>
                  </div>
                  <p className="text-body text-default-font">
                    You have a pending transfer to{' '}
                    <Link to={getWalletUrl(outgoingTransferOffer.toAddress)} className="text-brand-primary hover:underline">
                      {formatAddress(outgoingTransferOffer.toAddress)}
                    </Link>
                  </p>
                  <Button
                    variant="destructive-primary"
                    size="large"
                    onClick={handleCancelTransfer}
                    disabled={cancellingTransfer}
                    loading={cancellingTransfer}
                    className="w-full sm:w-auto"
                  >
                    Cancel Transfer
                  </Button>
                </div>
              )}

              {/* Active auction takes precedence over the standard Buy Now / List for Sale UI.
                  When an auction is active, the standard sell-price / "Not for Sale" boxes below
                  are hidden — see hasActiveAuction guards on each. */}
              {!nft.is_burned && auctionState.auction && (
                <ActiveAuctionView
                  auction={auctionState.auction}
                  bids={auctionState.bids}
                  onChange={() => { void auctionState.refresh(); }}
                />
              )}

              {/* Box 1: Not owner + Listed for sale (has currentSellPrice) */}
              {!nft.is_burned && !hasActiveAuction && !isOwner && currentSellPrice && (
                <div className={`flex w-full flex-col items-start gap-4 rounded-md border-2 border-solid ${
                  nft.collection?.bottom_floor_prices &&
                  nft.collection.bottom_floor_prices.length > 0 &&
                  Math.abs((currentSellPrice.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) < 0.01
                    ? 'border-green-700'
                    : 'border-neutral-border'
                } bg-default-background px-4 py-4 shadow-sm relative`}>
                  {nft.collection?.bottom_floor_prices &&
                   nft.collection.bottom_floor_prices.length > 0 && (() => {
                     const isAtFloor = Math.abs((currentSellPrice.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) < 0.01;
                     if (isAtFloor) {
                       return (
                         <span className="absolute top-4 right-4 text-caption-bold font-caption-bold text-green-700">
                           FLOOR
                         </span>
                       );
                     } else {
                       const percentageAboveFloor = ((currentSellPrice.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) / nft.collection.bottom_floor_prices[0].price * 100;
                       const isAbove10Percent = percentageAboveFloor > 10;
                       return (
                         <span className={`absolute top-4 right-4 text-caption-bold font-caption-bold ${
                           isAbove10Percent ? 'text-red-600' : 'text-subtext-color'
                         }`}>
                           {formatPercentageAboveFloor(percentageAboveFloor)}% above floor
                         </span>
                       );
                     }
                   })()}
                  <div className="flex w-full flex-col items-start gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-heading-1 font-heading-1 text-brand-700">
                        {formatXRP(currentSellPrice.amount)}
                      </span>
                      <Badge variant={currentSellPrice.destination ? 'brand' : 'success'}>
                        {getMarketplaceName(currentSellPrice.destination)}
                      </Badge>
                    </div>
                    {nft.collection?.bottom_floor_prices && nft.collection.bottom_floor_prices.length > 0 && (
                      <span className="text-caption font-caption text-subtext-color">
                        Floor (XRP):{' '}
                        {nft.collection.bottom_floor_prices.map((floorItem: { price: number; count: number }, index: number) => {
                          // Check if this price matches the current NFT's price
                          const matchesCurrentPrice = Math.abs((currentSellPrice.amount / 1_000_000) - floorItem.price) < 0.01;

                          // Only highlight the first matching price
                          const isFirstMatch = matchesCurrentPrice &&
                            nft.collection!.bottom_floor_prices!.findIndex((p: { price: number; count: number }) =>
                              Math.abs((currentSellPrice.amount / 1_000_000) - p.price) < 0.01
                            ) === index;

                          return (
                            <span key={index}>
                              <span className={isFirstMatch ? 'text-default-font font-bold' : ''}>
                                {floorItem.price.toLocaleString()}{floorItem.count > 1 ? ` (${floorItem.count})` : ''}
                              </span>
                              {index < nft.collection!.bottom_floor_prices!.length - 1 ? ', ' : ''}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex w-full items-center gap-2 flex-wrap mobile:gap-3">
                    {/* Buy Now - available to all users (guests use QR code) */}
                    <Button
                      className="h-10 w-auto flex-none mobile:flex-1 mobile:min-w-[120px]"
                      size="large"
                      disabled={processing || pendingSale || isRefetchingAfterTransaction}
                      onClick={handleBuyNow}
                    >
                      Buy Now
                    </Button>
                    {/* Make Offer - requires authentication */}
                    {isAuthenticated ? (
                      <>
                        <SubframeCore.Popover.Root open={offerPopoverOpen} onOpenChange={setOfferPopoverOpen}>
                          <SubframeCore.Popover.Trigger asChild={true}>
                            <Button
                              className="h-10 w-auto flex-none"
                              variant="neutral-secondary"
                              size="large"
                              disabled={effectiveOfferControls.offersDisabled}
                            >
                              Make Offer
                            </Button>
                          </SubframeCore.Popover.Trigger>
                        <SubframeCore.Popover.Portal>
                          <SubframeCore.Popover.Content
                            side="bottom"
                            align="center"
                            sideOffset={4}
                            asChild={true}
                          >
                            <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                              <span className="text-body-bold font-body-bold text-default-font">
                                Make an Offer
                              </span>
                              <TextField
                                className="h-auto w-full flex-none"
                                label="Offer Amount (XRP)"
                              >
                                <TextField.Input
                                  type="number"
                                  placeholder="Enter amount"
                                  min={effectiveOfferControls.effectiveMinimum ?? undefined}
                                  step="any"
                                  value={offerAmount}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setOfferAmount(event.target.value)
                                  }
                                />
                              </TextField>
                              {effectiveOfferControls.effectiveMinimum && (
                                <span className="text-caption font-caption text-subtext-color -mt-2">
                                  Offer controls: minimum {formatPrice(effectiveOfferControls.effectiveMinimum)}
                                </span>
                              )}
                              <TextField
                                className="h-auto w-full flex-none"
                                label="Expiration (days)"
                                helpText="Optional"
                              >
                                <TextField.Input
                                  placeholder="e.g., 7"
                                  value={offerExpiration}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setOfferExpiration(event.target.value)
                                  }
                                />
                              </TextField>
                              <div className="flex w-full flex-col gap-2">
                                <Select
                                  label="Marketplace"
                                  helpText=""
                                  value={offerMarketplace}
                                  onValueChange={(value) => setOfferMarketplace(value)}
                                >
                                  <Select.Content>
                                    {ownerHasAccount && (
                                      <Select.Item value="p2p">Direct P2P</Select.Item>
                                    )}
                                    {MARKETPLACE_OPTIONS.filter(opt => opt.value !== 'p2p').map(opt => (
                                      <Select.Item key={opt.value} value={opt.value}>{opt.label}</Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select>
                                <span className="text-caption font-caption text-subtext-color">
                                  {offerMarketplace === 'p2p'
                                    ? 'No marketplace fees. Owner will receive an email with offer details.'
                                    : `Owner will pay ${getMarketplaceName(getMarketplaceOption(offerMarketplace)?.destination)} fee of ${getMarketplaceFeePercentage(getMarketplaceOption(offerMarketplace)?.destination)}%`}
                                </span>
                              </div>
                              <div className="flex w-full items-center gap-2">
                                <SubframeCore.Popover.Close asChild={true}>
                                  <Button
                                    className="h-10 w-auto flex-none grow"
                                    variant="neutral-secondary"
                                    size="large"
                                    onClick={() => {
                                      setOfferAmount('');
                                      setOfferExpiration('');
                                      setActionError(null);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </SubframeCore.Popover.Close>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  size="large"
                                  onClick={handleMakeOffer}
                                  disabled={processing || (effectiveOfferControls.effectiveMinimum !== null && (!offerAmount || parseFloat(offerAmount) < effectiveOfferControls.effectiveMinimum))}
                                >
                                  {processing ? 'Creating...' : 'Submit Offer'}
                                </Button>
                              </div>
                            </div>
                          </SubframeCore.Popover.Content>
                        </SubframeCore.Popover.Portal>
                      </SubframeCore.Popover.Root>
                      {!isOwner && nft?.owner_address && (
                        <Button
                          className="h-10 w-auto flex-none"
                          variant="neutral-secondary"
                          size="large"
                          icon={<FeatherArrowLeftRight className="w-4 h-4" />}
                          onClick={() => setShowTradeBuilder(true)}
                        >
                          Propose Trade
                        </Button>
                      )}
                      {/* Add to Cart */}
                      {featureFlags.shoppingCart && currentSellPrice && (
                        <Button
                          className="h-10 w-auto flex-none"
                          variant="neutral-secondary"
                          size="large"
                          disabled={cartStore.isInCart(nft.nft_token_id) || processing}
                          onClick={() => {
                            const amountDrops = Number(currentSellPrice.amount);
                            const cartItem: CartItem = {
                              nft_token_id: nft.nft_token_id,
                              name: nft.name,
                              thumbnail_url: nft.thumbnail_url || null,
                              image_url: nft.image_url,
                              collection_name: nft.collection?.name || null,
                              collection_slug: nft.collection?.slug || null,
                              owner_address: nft.owner_address,
                              offer_id: currentSellPrice.offerIndex,
                              price_drops: amountDrops,
                              price_xrp: amountDrops / 1_000_000,
                              destination: currentSellPrice.destination || null,
                              expiration: currentSellPrice.expiresAt || null,
                              added_at: Date.now(),
                              rarity_rank: nft.rarity_rank || null,
                            };
                            const added = cartStore.addItem(cartItem);
                            if (added) {
                              showSuccess('Added to cart');
                            } else if (cartStore.isInCart(nft.nft_token_id)) {
                              showError('Already in cart');
                            } else if (cartStore.items.length >= 20) {
                              showError('Cart is full (max 20 items)');
                            } else {
                              showError('Cannot add to cart');
                            }
                          }}
                        >
                          <FeatherShoppingCart className="h-4 w-4 mr-1" />
                          {cartStore.isInCart(nft.nft_token_id) ? 'In Cart' : 'Add to Cart'}
                        </Button>
                      )}
                      </>
                    ) : (
                      <span className="text-caption font-caption text-subtext-color">
                        Sign in to make offers
                      </span>
                    )}
                  </div>
                  {/* Offers disabled message */}
                  {isAuthenticated && effectiveOfferControls.offersDisabled && (
                    <div className="flex items-center gap-1.5">
                      <FeatherX className="h-3.5 w-3.5 text-error-600 flex-shrink-0" />
                      <span className="text-caption font-caption text-subtext-color">
                        {effectiveOfferControls.source === 'wallet' || effectiveOfferControls.source === 'both'
                          ? 'Owner has disabled offers on all their NFTs'
                          : 'Owner has disabled offers on this NFT'}
                      </span>
                    </div>
                  )}
                  {/* Contest eligibility indicator */}
                  {hasActiveContest && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {isContestEligible ? (
                        <>
                          <FeatherCheck className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs text-green-500">Eligible for the active sweepstakes contest</span>
                        </>
                      ) : (
                        <>
                          <FeatherX className="h-3.5 w-3.5 text-red-400" />
                          <span className="text-xs text-red-400">Not eligible for the active sweepstakes contest</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Box 2: Not owner + Not listed for sale (no currentSellPrice) */}
              {!nft.is_burned && !hasActiveAuction && !isOwner && !currentSellPrice && (
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
                  <div className="flex w-full items-center gap-2">
                    <span className="line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-brand-700">
                      Not for Sale
                    </span>
                  </div>
                  {isAuthenticated && (
                    <>
                      {/* Offers disabled message */}
                      {offerControls.offersDisabled && (
                        <div className="flex w-full items-center gap-2 rounded-md bg-gray-100 px-3 py-2">
                          <span className="text-caption font-caption text-gray-600">
                            ⛔ Owner has disabled offers on this NFT
                          </span>
                        </div>
                      )}
                      <div className="flex w-full items-center gap-2">
                        <SubframeCore.Popover.Root open={offerPopoverOpen} onOpenChange={setOfferPopoverOpen}>
                          <SubframeCore.Popover.Trigger asChild={true}>
                            <Button
                              className="h-10 w-auto flex-none"
                              variant="neutral-primary"
                              size="large"
                              disabled={offerControls.offersDisabled}
                            >
                              Make Offer
                            </Button>
                          </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Make an Offer
                            </span>
                            <TextField
                              className="h-auto w-full flex-none"
                              label="Offer Amount (XRP)"
                            >
                              <TextField.Input
                                placeholder="Enter amount"
                                value={offerAmount}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setOfferAmount(event.target.value)
                                }
                              />
                            </TextField>
                            {effectiveOfferControls.effectiveMinimum && (
                              <span className="text-caption font-caption text-subtext-color -mt-2">
                                Offer controls: minimum {formatPrice(effectiveOfferControls.effectiveMinimum)}
                              </span>
                            )}
                            <TextField
                              className="h-auto w-full flex-none"
                              label="Expiration (days)"
                              helpText="Optional"
                            >
                              <TextField.Input
                                placeholder="e.g., 7"
                                value={offerExpiration}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setOfferExpiration(event.target.value)
                                }
                              />
                            </TextField>
                            <div className="flex w-full flex-col gap-2">
                              <Select
                                label="Marketplace"
                                helpText=""
                                value={offerMarketplace}
                                onValueChange={(value) => setOfferMarketplace(value)}
                              >
                                <Select.Content>
                                  {ownerHasAccount && (
                                    <Select.Item value="p2p">Direct P2P</Select.Item>
                                  )}
                                  {MARKETPLACE_OPTIONS.filter(opt => opt.value !== 'p2p').map(opt => (
                                    <Select.Item key={opt.value} value={opt.value}>{opt.label}</Select.Item>
                                  ))}
                                </Select.Content>
                              </Select>
                              <span className="text-caption font-caption text-subtext-color">
                                {offerMarketplace === 'p2p'
                                  ? 'No marketplace fees. Owner will receive an email with offer details.'
                                  : `Owner will pay ${getMarketplaceName(getMarketplaceOption(offerMarketplace)?.destination)} fee of ${getMarketplaceFeePercentage(getMarketplaceOption(offerMarketplace)?.destination)}%`}
                              </span>
                            </div>
                            <div className="flex w-full items-center gap-2">
                              <SubframeCore.Popover.Close asChild={true}>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  variant="neutral-secondary"
                                  size="large"
                                  onClick={() => {
                                    setOfferAmount('');
                                    setOfferExpiration('');
                                    setActionError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </SubframeCore.Popover.Close>
                              <Button
                                className="h-10 w-auto flex-none grow"
                                size="large"
                                onClick={handleMakeOffer}
                                disabled={processing || (effectiveOfferControls.effectiveMinimum !== null && (!offerAmount || parseFloat(offerAmount) < effectiveOfferControls.effectiveMinimum))}
                              >
                                {processing ? 'Creating...' : 'Submit Offer'}
                              </Button>
                            </div>
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                        </SubframeCore.Popover.Root>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Ambassador Lock Banner - owner only */}
              {isOwner && ambassadorLock && (
                <div className={`flex w-full flex-col items-start gap-2 rounded-md border-2 px-4 py-3 ${
                  ambassadorLock.expired
                    ? 'border-success-300 bg-success-50'
                    : 'border-warning-300 bg-warning-50'
                }`}>
                  {ambassadorLock.expired ? (
                    <>
                      <span className="text-body font-body-bold text-success-700">Ambassador Hold Period Ended</span>
                      <span className="text-caption text-success-600">
                        This NFT was received through the Ambassadors Program. The hold period has ended &mdash; you may sell or transfer it freely.
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-body font-body-bold text-warning-700">Ambassador Hold Period Active</span>
                      <span className="text-caption text-warning-600">
                        This NFT was received through the Ambassadors Program. It is under a hold period until{' '}
                        {new Date(ambassadorLock.lockExpiresAt).toLocaleDateString()}.
                        Selling, transferring, or listing this NFT before that date will significantly impact your Ambassador score.
                      </span>
                      <span className="text-caption text-warning-500">
                        {Math.ceil((new Date(ambassadorLock.lockExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days remaining
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Box 3: Owner + Not listed for sale (no activeSellOffer) */}
              {!nft.is_burned && !hasActiveAuction && isOwner && !activeSellOffer && (
                <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
                  <div className="flex w-full items-start gap-2">
                    <span className="line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-brand-700">
                      You own this NFT
                    </span>
                  </div>
                  {/* Sell Now to Collection Offer - on its own row */}
                  {bestCollectionOffer && (
                    <button
                      onClick={() => setShowAcceptCollectionOfferModal(true)}
                      disabled={processing}
                      className="h-10 px-4 rounded-md bg-success-600 hover:bg-success-700 text-white font-semibold text-body transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Sell Now for {formatPrice(bestCollectionOffer.sellerReceivesXrp)}
                    </button>
                  )}
                  <div className="flex w-full items-center gap-2 flex-wrap mobile:gap-3">
                    {/* List for Sale Popover */}
                    <SubframeCore.Popover.Root open={sellPopoverOpen} onOpenChange={setSellPopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          className="h-10 w-auto flex-none"
                          size="large"
                          disabled={processing}
                        >
                          List for Sale
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                            <Tabs>
                              <Tabs.Item active={sellPopoverMode === 'sell'} onClick={() => setSellPopoverMode('sell')}>
                                Sell
                              </Tabs.Item>
                              <Tabs.Item active={sellPopoverMode === 'auction'} onClick={() => setSellPopoverMode('auction')}>
                                Auction
                              </Tabs.Item>
                            </Tabs>
                            {sellPopoverMode === 'auction' ? (
                              <AuctionListingForm
                                nftTokenId={tokenId!}
                                onConfirmed={() => {
                                  setSellPopoverOpen(false);
                                  setSellPopoverMode('sell');
                                  // Immediately refresh so the auction view appears without
                                  // waiting for the 15s poll interval.
                                  void auctionState.refresh();
                                }}
                                onCancel={() => setSellPopoverOpen(false)}
                              />
                            ) : (
                            <>
                            <TextField
                              className="h-auto w-full flex-none"
                              label="Sale Price (XRP)"
                              helpText=""
                            >
                              <TextField.Input
                                placeholder="e.g., 100"
                                value={sellPrice}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setSellPrice(event.target.value)
                                }
                              />
                            </TextField>
                            <TextField
                              className="h-auto w-full flex-none"
                              label="Expiration (days)"
                              helpText="Optional"
                            >
                              <TextField.Input
                                placeholder="e.g., 30"
                                value={sellExpiration}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setSellExpiration(event.target.value)
                                }
                              />
                            </TextField>
                            <div className="flex w-full flex-col gap-2">
                              <Select
                                className="h-auto w-full flex-none"
                                label="Marketplace"
                                helpText=""
                                value={sellMarketplace}
                                onValueChange={(value) => setSellMarketplace(value)}
                              >
                                <Select.Content>
                                  {MARKETPLACE_OPTIONS.map(opt => (
                                    <Select.Item key={opt.value} value={opt.value}>{opt.label}</Select.Item>
                                  ))}
                                </Select.Content>
                              </Select>
                              <span className="text-caption font-caption text-subtext-color">
                                {sellMarketplace === 'p2p'
                                  ? 'No marketplace fees. Open sell offer with no destination.'
                                  : `Buyer will pay ${getMarketplaceName(getMarketplaceOption(sellMarketplace)?.destination)} fee of ${getMarketplaceFeePercentage(getMarketplaceOption(sellMarketplace)?.destination)}%`}
                              </span>
                            </div>
                            <div className="flex w-full items-center gap-2">
                              <SubframeCore.Popover.Close asChild={true}>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  variant="neutral-secondary"
                                  size="large"
                                  onClick={() => {
                                    setSellPrice('');
                                    setSellExpiration('');
                                    setActionError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </SubframeCore.Popover.Close>
                              <Button
                                className="h-10 w-auto flex-none grow"
                                size="large"
                                onClick={handleSell}
                                disabled={processing}
                              >
                                {processing ? 'Creating...' : 'Create Sell Offer'}
                              </Button>
                            </div>
                            </>
                            )}
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>

                    {/* Transfer Popover */}
                    <SubframeCore.Popover.Root open={transferPopoverOpen} onOpenChange={setTransferPopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          className="h-10 w-auto flex-none"
                          variant="neutral-primary"
                          size="large"
                          disabled={processing}
                        >
                          Transfer
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Transfer NFT
                            </span>
                            <TextField
                              className="h-auto w-full flex-none"
                              label="Recipient Address"
                              helpText=""
                            >
                              <TextField.Input
                                placeholder="rXXXXXXXXXXXXXXXXXXXXXXXX"
                                value={transferAddress}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                  setTransferAddress(event.target.value)
                                }
                              />
                            </TextField>
                            <div className="flex w-full items-center gap-2">
                              <SubframeCore.Popover.Close asChild={true}>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  variant="neutral-secondary"
                                  size="large"
                                  onClick={() => {
                                    setTransferAddress('');
                                    setActionError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </SubframeCore.Popover.Close>
                              <Button
                                className="h-10 w-auto flex-none grow"
                                size="large"
                                onClick={handleTransfer}
                                disabled={processing}
                              >
                                {processing ? 'Creating...' : 'Transfer'}
                              </Button>
                            </div>
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>

                    {/* Offer Controls Popover */}
                    <SubframeCore.Popover.Root open={offerControlsPopoverOpen} onOpenChange={setOfferControlsPopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          className="h-10 w-auto flex-none"
                          variant="neutral-secondary"
                          size="large"
                        >
                          Offer Controls
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Offer Controls
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              These controls apply to offers made on xrp.deals. Offers from other marketplaces aren't blocked, but you won't be notified for those that violate your settings.
                            </span>
                            <div className="flex w-full items-center gap-2">
                              <input
                                type="checkbox"
                                id="offersDisabled"
                                checked={offersDisabledForm}
                                onChange={(e) => setOffersDisabledForm(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              />
                              <label htmlFor="offersDisabled" className="text-body font-body text-default-font">
                                Disable all buy offers
                              </label>
                            </div>
                            {!offersDisabledForm && (
                              <TextField
                                className="h-auto w-full flex-none"
                                label="Minimum Buy Offer Amount (XRP)"
                                helpText="Leave empty to allow any amount"
                              >
                                <TextField.Input
                                  placeholder="e.g., 50"
                                  value={minOfferPriceForm}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setMinOfferPriceForm(event.target.value)
                                  }
                                />
                              </TextField>
                            )}
                            {actionError && (
                              <span className="text-caption font-caption text-error-700">
                                {actionError}
                              </span>
                            )}
                            <div className="flex w-full items-center gap-2">
                              <SubframeCore.Popover.Close asChild={true}>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  variant="neutral-secondary"
                                  size="large"
                                  onClick={() => {
                                    // Reset form to current values
                                    setOffersDisabledForm(offerControls.offersDisabled);
                                    setMinOfferPriceForm(offerControls.minOfferPrice?.toString() || '');
                                    setActionError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </SubframeCore.Popover.Close>
                              <Button
                                className="h-10 w-auto flex-none grow"
                                size="large"
                                onClick={handleUpdateOfferControls}
                                disabled={updatingOfferControls}
                              >
                                {updatingOfferControls ? 'Saving...' : 'Save Settings'}
                              </Button>
                            </div>
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>
                    {/* Feature Button */}
                    {promotionStatus?.isPromoted ? (
                      <SubframeCore.Tooltip.Provider>
                        <SubframeCore.Tooltip.Root>
                          <SubframeCore.Tooltip.Trigger asChild>
                            <div className="flex h-10 items-center gap-2 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 px-4 shadow-sm cursor-default">
                              <FeatherStar className="h-4 w-4 text-white fill-white" />
                              <span className="text-sm font-semibold text-white">Featured</span>
                            </div>
                          </SubframeCore.Tooltip.Trigger>
                          <SubframeCore.Tooltip.Portal>
                            <SubframeCore.Tooltip.Content side="bottom" align="center" sideOffset={8} asChild>
                              <Tooltip>
                                Featured for {formatDistance(new Date(), new Date(promotionStatus.promotion!.expiresAt))} more
                              </Tooltip>
                            </SubframeCore.Tooltip.Content>
                          </SubframeCore.Tooltip.Portal>
                        </SubframeCore.Tooltip.Root>
                      </SubframeCore.Tooltip.Provider>
                    ) : (
                      <Button
                        className="h-10 w-auto flex-none border-2 border-amber-400"
                        variant="neutral-secondary"
                        size="large"
                        disabled={processing}
                        icon={<FeatherStar className="h-4 w-4 text-amber-500" />}
                        onClick={() => setShowPromotionDialog(true)}
                      >
                        Feature
                      </Button>
                    )}
                  </div>

                  {/* Show current offer settings indicator */}
                  {(effectiveOfferControls.offersDisabled || effectiveOfferControls.effectiveMinimum || effectiveOfferControls.noFloorPrice) && (
                    <span className="text-caption font-caption text-subtext-color">
                      {effectiveOfferControls.offersDisabled
                        ? effectiveOfferControls.source === 'wallet' || effectiveOfferControls.source === 'both'
                          ? 'Buy offers are disabled (wallet settings)'
                          : 'Buy offers are disabled for this NFT'
                        : effectiveOfferControls.noFloorPrice && !effectiveOfferControls.effectiveMinimum
                          ? 'No minimum (collection has no floor price)'
                          : `Minimum offer: ${formatPrice(effectiveOfferControls.effectiveMinimum!)}${
                              effectiveOfferControls.source === 'wallet' || effectiveOfferControls.source === 'both'
                                ? ' (wallet settings)'
                                : ''
                            }`}
                    </span>
                  )}
                </div>
              )}

              {/* Box 4: Owner + Listed for sale (has activeSellOffer) */}
              {!nft.is_burned && !hasActiveAuction && isOwner && activeSellOffer && (
                <div className={`flex w-full flex-col items-start gap-4 rounded-md border-2 border-solid ${
                  nft.collection?.bottom_floor_prices &&
                  nft.collection.bottom_floor_prices.length > 0 &&
                  Math.abs((activeSellOffer.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) < 0.01
                    ? 'border-green-700'
                    : 'border-neutral-border'
                } bg-default-background px-4 py-4 shadow-sm relative`}>
                  {nft.collection?.bottom_floor_prices &&
                   nft.collection.bottom_floor_prices.length > 0 && (() => {
                     const isAtFloor = Math.abs((activeSellOffer.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) < 0.01;
                     if (isAtFloor) {
                       return (
                         <span className="absolute top-4 right-4 text-caption-bold font-caption-bold text-green-700">
                           FLOOR
                         </span>
                       );
                     } else {
                       const percentageAboveFloor = ((activeSellOffer.amount / 1_000_000) - nft.collection.bottom_floor_prices[0].price) / nft.collection.bottom_floor_prices[0].price * 100;
                       const isAbove10Percent = percentageAboveFloor > 10;
                       return (
                         <span className={`absolute top-4 right-4 text-caption-bold font-caption-bold ${
                           isAbove10Percent ? 'text-red-600' : 'text-subtext-color'
                         }`}>
                           {formatPercentageAboveFloor(percentageAboveFloor)}% above floor
                         </span>
                       );
                     }
                   })()}
                  <div className="flex w-full items-start gap-2">
                    <span className="line-clamp-1 grow shrink-0 basis-0 text-body-bold font-body-bold text-brand-700">
                      You own this NFT
                    </span>
                  </div>
                  <span className="text-heading-1 font-heading-1 text-brand-700">
                    {formatXRP(activeSellOffer.amount)}
                  </span>
                  {nft.collection?.bottom_floor_prices && nft.collection.bottom_floor_prices.length > 0 && (
                    <span className="text-caption font-caption text-subtext-color">
                      Floor (XRP):{' '}
                      {nft.collection.bottom_floor_prices.map((floorItem: { price: number; count: number }, index: number) => {
                        // Check if this price matches the current NFT's price
                        const matchesCurrentPrice = Math.abs((activeSellOffer.amount / 1_000_000) - floorItem.price) < 0.01;

                        // Only highlight the first matching price
                        const isFirstMatch = matchesCurrentPrice &&
                          nft.collection!.bottom_floor_prices!.findIndex((p: { price: number; count: number }) =>
                            Math.abs((activeSellOffer.amount / 1_000_000) - p.price) < 0.01
                          ) === index;

                        return (
                          <span key={index}>
                            <span className={isFirstMatch ? 'text-default-font font-bold' : ''}>
                              {floorItem.price.toLocaleString()}{floorItem.count > 1 ? ` (${floorItem.count})` : ''}
                            </span>
                            {index < nft.collection!.bottom_floor_prices!.length - 1 ? ', ' : ''}
                          </span>
                        );
                      })}
                    </span>
                  )}
                  <div className="flex w-full flex-wrap items-center gap-2 mobile:gap-3">
                    <Button
                      className="h-10 w-auto flex-none mobile:flex-1 mobile:min-w-[120px]"
                      variant="neutral-primary"
                      size="large"
                      disabled={processing}
                      onClick={handleCancelListing}
                    >
                      {processing ? 'Processing...' : 'Cancel Listing'}
                    </Button>
                    {/* Offer Controls Popover */}
                    <SubframeCore.Popover.Root open={offerControlsPopoverOpen} onOpenChange={setOfferControlsPopoverOpen}>
                      <SubframeCore.Popover.Trigger asChild={true}>
                        <Button
                          className="h-10 w-auto flex-none"
                          variant="neutral-secondary"
                          size="large"
                        >
                          Offer Controls
                        </Button>
                      </SubframeCore.Popover.Trigger>
                      <SubframeCore.Popover.Portal>
                        <SubframeCore.Popover.Content
                          side="bottom"
                          align="center"
                          sideOffset={4}
                          asChild={true}
                        >
                          <div className="flex w-80 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-lg">
                            <span className="text-body-bold font-body-bold text-default-font">
                              Offer Controls
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              These controls apply to offers made on xrp.deals. Offers from other marketplaces aren't blocked, but you won't be notified for those that violate your settings.
                            </span>
                            <div className="flex w-full items-center gap-2">
                              <input
                                type="checkbox"
                                id="offersDisabledListed"
                                checked={offersDisabledForm}
                                onChange={(e) => setOffersDisabledForm(e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              />
                              <label htmlFor="offersDisabledListed" className="text-body font-body text-default-font">
                                Disable all buy offers
                              </label>
                            </div>
                            {!offersDisabledForm && (
                              <TextField
                                className="h-auto w-full flex-none"
                                label="Minimum Buy Offer Amount (XRP)"
                                helpText="Leave empty to allow any amount"
                              >
                                <TextField.Input
                                  placeholder="e.g., 50"
                                  value={minOfferPriceForm}
                                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    setMinOfferPriceForm(event.target.value)
                                  }
                                />
                              </TextField>
                            )}
                            {actionError && (
                              <span className="text-caption font-caption text-error-700">
                                {actionError}
                              </span>
                            )}
                            <div className="flex w-full items-center gap-2">
                              <SubframeCore.Popover.Close asChild={true}>
                                <Button
                                  className="h-10 w-auto flex-none grow"
                                  variant="neutral-secondary"
                                  size="large"
                                  onClick={() => {
                                    // Reset form to current values
                                    setOffersDisabledForm(offerControls.offersDisabled);
                                    setMinOfferPriceForm(offerControls.minOfferPrice?.toString() || '');
                                    setActionError(null);
                                  }}
                                >
                                  Cancel
                                </Button>
                              </SubframeCore.Popover.Close>
                              <Button
                                className="h-10 w-auto flex-none grow"
                                size="large"
                                onClick={handleUpdateOfferControls}
                                disabled={updatingOfferControls}
                              >
                                {updatingOfferControls ? 'Saving...' : 'Save Settings'}
                              </Button>
                            </div>
                          </div>
                        </SubframeCore.Popover.Content>
                      </SubframeCore.Popover.Portal>
                    </SubframeCore.Popover.Root>
                    {/* Feature Button */}
                    {promotionStatus?.isPromoted ? (
                      <SubframeCore.Tooltip.Provider>
                        <SubframeCore.Tooltip.Root>
                          <SubframeCore.Tooltip.Trigger asChild>
                            <div className="flex h-10 items-center gap-2 rounded-md bg-gradient-to-r from-amber-500 to-amber-400 px-4 shadow-sm cursor-default">
                              <FeatherStar className="h-4 w-4 text-white fill-white" />
                              <span className="text-sm font-semibold text-white">Featured</span>
                            </div>
                          </SubframeCore.Tooltip.Trigger>
                          <SubframeCore.Tooltip.Portal>
                            <SubframeCore.Tooltip.Content side="bottom" align="center" sideOffset={8} asChild>
                              <Tooltip>
                                Featured for {formatDistance(new Date(), new Date(promotionStatus.promotion!.expiresAt))} more
                              </Tooltip>
                            </SubframeCore.Tooltip.Content>
                          </SubframeCore.Tooltip.Portal>
                        </SubframeCore.Tooltip.Root>
                      </SubframeCore.Tooltip.Provider>
                    ) : (
                      <Button
                        className="h-10 w-auto flex-none border-2 border-amber-400"
                        variant="neutral-secondary"
                        size="large"
                        disabled={processing}
                        icon={<FeatherStar className="h-4 w-4 text-amber-500" />}
                        onClick={() => setShowPromotionDialog(true)}
                      >
                        Feature
                      </Button>
                    )}
                  </div>

                  {/* Show current offer settings indicator */}
                  {(effectiveOfferControls.offersDisabled || effectiveOfferControls.effectiveMinimum || effectiveOfferControls.noFloorPrice) && (
                    <span className="text-caption font-caption text-subtext-color">
                      {effectiveOfferControls.offersDisabled
                        ? effectiveOfferControls.source === 'wallet' || effectiveOfferControls.source === 'both'
                          ? 'Buy offers are disabled (wallet settings)'
                          : 'Buy offers are disabled for this NFT'
                        : effectiveOfferControls.noFloorPrice && !effectiveOfferControls.effectiveMinimum
                          ? 'No minimum (collection has no floor price)'
                          : `Minimum offer: ${formatPrice(effectiveOfferControls.effectiveMinimum!)}${
                              effectiveOfferControls.source === 'wallet' || effectiveOfferControls.source === 'both'
                                ? ' (wallet settings)'
                                : ''
                            }`}
                    </span>
                  )}
                  {/* Contest eligibility indicator (owner view) */}
                  {hasActiveContest && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {isContestEligible ? (
                        <>
                          <FeatherCheck className="h-3.5 w-3.5 text-green-500" />
                          <span className="text-xs text-green-500">This listing is eligible for the active sweepstakes contest</span>
                        </>
                      ) : (
                        <>
                          <FeatherX className="h-3.5 w-3.5 text-red-400" />
                          <span className="text-xs text-red-400">This listing is not eligible for the active sweepstakes contest</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* User's Active Buy Offers */}
            {!isOwner && userBuyOffers.length > 0 && (
              <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4 shadow-sm">
                <span className="text-body-bold font-body-bold text-default-font">
                  Your Active Offers
                </span>
                {userBuyOffers.map((offer) => (
                  <div
                    key={offer.offerIndex}
                    className="w-full flex items-center justify-between rounded-md bg-brand-100 border border-solid border-brand-600 px-3 py-2"
                  >
                    <div className="flex flex-col">
                      <span className="text-body-bold font-body-bold text-brand-800">
                        💰 {formatXRP(offer.amount)}
                      </span>
                      {offer.expiresAt && (
                        <span className="text-caption font-caption text-brand-700">
                          Expires: {new Date(offer.expiresAt * 1000).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <Button
                      size="small"
                      variant="destructive-primary"
                      onClick={() => handleCancelBuyOffer(offer.offerIndex)}
                      disabled={processing}
                    >
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Details */}
            <div className="flex w-full flex-col items-start gap-3">
              <div className="flex w-full items-center gap-2">
                <div className="flex grow shrink-0 basis-0 items-center gap-2">
                  <FeatherUser className="text-body font-body text-subtext-color" />
                  <span className="text-body font-body text-subtext-color">Owner</span>
                </div>
                <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                  <Link
                    to={getWalletUrl(nft.owner_address)}
                    className="text-body font-body text-brand-700 hover:text-brand-600 underline"
                  >
                    {formatAddress(nft.owner_address)}
                  </Link>
                  {isOwner && (
                    <Badge variant="brand">You</Badge>
                  )}
                  {isAuthenticated && !isOwner && (
                    <IconButton
                      size="small"
                      icon={<FeatherMessageCircle className="w-4 h-4" />}
                      onClick={() => navigate(`/inbox?start=${nft.owner_address}`)}
                    />
                  )}
                </div>
              </div>

              {/* Owner Acquisition Info */}
              {currentOwnerAcquisition && currentOwnerAcquisition.acquiredVia && (
                <div className="flex w-full flex-col items-start">
                  <span className="text-caption font-caption text-neutral-400">
                    {currentOwnerAcquisition.acquiredVia === 'mint' && (
                      <>Minted {formatHoldTime(currentOwnerAcquisition.currentHoldTimeSeconds)} ago</>
                    )}
                    {currentOwnerAcquisition.acquiredVia === 'purchase' && currentOwnerAcquisition.acquiredPrice && (
                      <>Bought for {formatPrice(currentOwnerAcquisition.acquiredPrice)} {formatHoldTime(currentOwnerAcquisition.currentHoldTimeSeconds)} ago</>
                    )}
                    {currentOwnerAcquisition.acquiredVia === 'transfer' && (
                      <>Received via transfer {formatHoldTime(currentOwnerAcquisition.currentHoldTimeSeconds)} ago</>
                    )}
                  </span>
                </div>
              )}

              <div className="flex w-full items-center gap-2">
                <div className="flex grow shrink-0 basis-0 items-center gap-2">
                  <FeatherTag className="text-body font-body text-subtext-color" />
                  <span className="text-body font-body text-subtext-color">Token ID</span>
                </div>
                <div className="flex grow shrink-0 basis-0 items-center justify-end gap-2">
                  <span className="text-body font-body text-default-font font-mono text-xs">
                    {formatTokenId(nft.nft_token_id)}
                  </span>
                  <button
                    onClick={() => copyToClipboard(nft.nft_token_id, 'NFT ID copied to clipboard')}
                    className="flex-shrink-0 p-1 hover:bg-neutral-100 rounded cursor-pointer transition-colors"
                    title="Copy Token ID"
                  >
                    <FeatherCopy className="text-body font-body text-subtext-color hover:text-brand-700" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Build Your Own CTA - shown for NFTs in composable collections with compose enabled */}
        {featureFlags.composable && !nft.is_burned && isOwner && nft.collection?.slug && nft.collection?.compose_enabled && (
          <Alert
            variant="warning"
            icon={<FeatherZap />}
            title="Part of a Build Your Own collection — combine with other NFTs to create something new!"
            actions={
              <Link to={`/collections/${nft.collection.slug}/compose?nft=${nft.nft_token_id}`}>
                <Button variant="brand-secondary" size="medium" icon={<FeatherZap />}>
                  Build
                </Button>
              </Link>
            }
          />
        )}

        {/* Traits Section */}
        {nft.traits_with_rarity && nft.traits_with_rarity.length > 0 && (() => {
          const traitRarityLookup = createTraitRarityLookup(nft.collection?.trait_stats);
          return (
          <div className="flex w-full flex-col items-start gap-6 overflow-hidden">
            <span className="text-heading-2 font-heading-2 text-default-font">Traits</span>
            <div className="w-full items-start gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 max-w-full">
              {nft.traits_with_rarity.map((trait: any) => {
                const percentage = trait.rarity_percentage || trait.percentage;
                const barWidth = percentage ? `${Math.min(percentage, 100)}%` : '0%';
                const rarityInfo = traitRarityLookup(trait.trait_type, percentage);

                // Calculate price comparison: percentage above floor
                let priceComparison: number | null = null;
                let isAtFloor = false;
                if (nft.min_sell_price && trait.floor_price !== null && trait.floor_price !== undefined) {
                  priceComparison = ((nft.min_sell_price - trait.floor_price) / trait.floor_price) * 100;
                  // Use tolerance of 0.01% to handle floating point precision issues
                  isAtFloor = Math.abs(priceComparison) <= 0.01;
                }

                const traitFilter = {
                  [trait.trait_type]: [trait.value]
                };
                const collectionUrl = nft.collection
                  ? `/collections/${nft.collection.slug || nft.collection.id}?traits=${encodeURIComponent(JSON.stringify(traitFilter))}&sort=price-low-high`
                  : '#';

                return (
                  <Link
                    to={collectionUrl}
                    key={trait.trait_type + trait.value}
                    className={`flex flex-col items-start gap-4 rounded-md border-2 border-solid bg-default-background px-4 py-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow min-w-0 overflow-hidden ${
                      isAtFloor ? 'border-green-700' : 'border-neutral-border'
                    }`}
                  >
                    <div className="flex w-full flex-col items-start gap-1">
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="line-clamp-1 text-caption-bold font-caption-bold text-subtext-color uppercase">
                          {trait.trait_type}
                        </span>
                        {priceComparison !== null && (
                          <BlurredPremiumFeature label="floor comparison" inline>
                            <span className={`text-caption-bold font-caption-bold uppercase flex-shrink-0 ${
                              isAtFloor ? 'text-green-700' : priceComparison > 10 ? 'text-red-600' : 'text-subtext-color'
                            }`}>
                              {isAtFloor ? 'FLOOR' : `+${formatPercentageAboveFloor(priceComparison)}%`}
                            </span>
                          </BlurredPremiumFeature>
                        )}
                      </div>
                      <span
                        className={`w-full text-heading-3 font-heading-3 ${
                          rarityInfo.tier === 'legendary' ? 'legendary-shimmer' : ''
                        }`}
                        style={rarityInfo.tier === 'legendary' ? undefined : { color: rarityInfo.color }}
                      >
                        {trait.value}
                      </span>
                    </div>
                    <div className="flex w-full flex-col items-start gap-2">
                      <div className="flex w-full items-center gap-2">
                        <div className="flex w-1/2 items-center gap-2 min-w-0">
                          <div className="flex h-1.5 flex-1 flex-col items-center gap-2 rounded-full bg-neutral-200 relative min-w-0">
                            <div
                              className="flex h-1.5 flex-none flex-col items-center gap-2 rounded-full absolute left-0"
                              style={{ width: barWidth, backgroundColor: rarityInfo.color }}
                            />
                          </div>
                          <span className="text-caption-bold font-caption-bold text-default-font whitespace-nowrap">
                            {percentage ? `${percentage.toFixed(1)}%` : 'N/A'}
                            {trait.count && ` (${trait.count.toLocaleString()})`}
                          </span>
                        </div>
                        <div className="flex w-1/2 justify-end">
                          <span
                            className={`text-caption-bold font-caption-bold whitespace-nowrap ${
                              rarityInfo.tier === 'legendary' ? 'legendary-shimmer' : ''
                            }`}
                            style={{ color: rarityInfo.color }}
                          >
                            {rarityInfo.label}
                          </span>
                        </div>
                      </div>
                      <BlurredPremiumFeature label="trait floor prices" compact>
                        {trait.floor_prices !== undefined && trait.floor_prices.length > 0 ? (
                          <span className="text-caption font-caption text-subtext-color">
                            Floor (XRP):{' '}
                            {trait.floor_prices.map((price: number, index: number) => {
                              // Check if this price matches the current NFT's price
                              const matchesCurrentPrice = nft.min_sell_price !== null &&
                                nft.min_sell_price !== undefined &&
                                Math.abs(price - nft.min_sell_price) < 0.01;

                              // Only highlight the first matching price
                              const isFirstMatch = matchesCurrentPrice &&
                                trait.floor_prices!.findIndex((p: number) =>
                                  nft.min_sell_price !== null &&
                                  Math.abs(p - nft.min_sell_price!) < 0.01
                                ) === index;

                              return (
                                <span key={index}>
                                  <span className={isFirstMatch ? 'text-default-font font-bold' : ''}>
                                    {price.toLocaleString()}
                                  </span>
                                  {index < trait.floor_prices!.length - 1 ? ', ' : ''}
                                </span>
                              );
                            })}
                          </span>
                        ) : trait.floor_price !== undefined ? (
                          <span className="text-caption font-caption text-subtext-color">
                            Floor: {trait.floor_price !== null ? formatPrice(trait.floor_price) : 'No listings'}
                          </span>
                        ) : null}
                      </BlurredPremiumFeature>
                      {trait.last_sale_price !== undefined && (
                        <span className="text-caption font-caption text-subtext-color">
                          Last sale: {trait.last_sale_price !== null && trait.last_sale_date !== null
                            ? `${formatPrice(trait.last_sale_price)} (${formatTimeSinceSale(trait.last_sale_date)})`
                            : 'N/A'}
                        </span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
          );
        })()}

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Buy Offers - Always Visible */}
        {buyOffers.length > 0 ? (
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Buy Offers
            </span>
            <div className="w-full overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>From</Table.HeaderCell>
                  <Table.HeaderCell>Amount</Table.HeaderCell>
                  <Table.HeaderCell>Type</Table.HeaderCell>
                  <Table.HeaderCell>Expiration</Table.HeaderCell>
                  {isOwner && <Table.HeaderCell />}
                </Table.HeaderRow>
              }
            >
              {buyOffers.slice(0, 10).map((offer) => (
                <Table.Row key={offer.offerIndex}>
                  <Table.Cell>
                    <Link
                      to={getWalletUrl(offer.creator)}
                      className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                    >
                      {formatAddress(offer.creator)}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {formatXRP(offer.amount)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant={offer.destination ? 'brand' : 'success'}>
                      {getMarketplaceName(offer.destination)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-default-font">
                      {offer.expiresAt ? `in ${Math.ceil((offer.expiresAt * 1000 - Date.now()) / (1000 * 60 * 60 * 24))} days` : 'No expiration'}
                    </span>
                  </Table.Cell>
                  {isOwner && (
                    <Table.Cell>
                      <div className="flex grow shrink-0 basis-0 items-center justify-end">
                        <Button
                          size="small"
                          onClick={() => handleAcceptBuyOffer(offer)}
                          disabled={processing}
                        >
                          Accept
                        </Button>
                      </div>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table>
            </div>
          </div>
        ) : (
          <>
            <span className="text-heading-3 font-heading-3 text-default-font">
              Buy Offers
            </span>
            <span className="text-body font-body text-subtext-color">
              No offers for this NFT
            </span>
            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
          </>
        )}

        {/* Sale History */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-3 font-heading-3 text-default-font">Sale History</span>
          {loadingHistory ? (
            <div className="w-full text-center py-8 text-subtext-color">Loading transaction history...</div>
          ) : transactions.length === 0 ? (
            <>
              <div className="text-body font-body text-subtext-color">No transaction history available</div>
              <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
            </>
          ) : (
            <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <Table className="min-w-[700px]"
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>Event</Table.HeaderCell>
                  <Table.HeaderCell>Price</Table.HeaderCell>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>From</Table.HeaderCell>
                  <Table.HeaderCell>To</Table.HeaderCell>
                  <Table.HeaderCell>Seller Metrics</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {transactions.map((tx) => {
                // For auction settlements, the on-chain tx Amount is the
                // sell-offer (reserve net of fee) — the actual sale price is
                // the winning bid. Prefer that when present.
                const displayAmount = tx.auction_winning_bid_drops
                  ? Number(tx.auction_winning_bid_drops)
                  : (tx.amount || tx.price_xrp);
                return (
                <Table.Row key={tx.id}>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      {getTransactionIcon(tx.type || tx.transaction_type)}
                      <span className="whitespace-nowrap text-body-bold font-body-bold text-neutral-700">
                        {getTransactionLabel(tx.type || tx.transaction_type)}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {displayAmount ? formatXRP(displayAmount) : '–'}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      className="whitespace-nowrap text-body font-body text-default-font"
                      title={format(new Date(tx.timestamp || tx.occurred_at), 'PPpp')}
                    >
                      {formatDistanceToNow(new Date(tx.timestamp || tx.occurred_at), { addSuffix: true }).replace(/^about /, '')}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    {tx.from || tx.from_address ? (
                      <Link
                        to={getWalletUrl(tx.from || tx.from_address)}
                        className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                      >
                        {formatAddress(tx.from || tx.from_address)}
                      </Link>
                    ) : (
                      <span className="whitespace-nowrap text-body font-body text-subtext-color">
                        –
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {tx.to || tx.to_address ? (
                      <Link
                        to={getWalletUrl(tx.to || tx.to_address)}
                        className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                      >
                        {formatAddress(tx.to || tx.to_address)}
                      </Link>
                    ) : (
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        –
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {(() => {
                      const metrics = formatSellerMetrics(
                        tx.from_address_hold_duration_seconds,
                        tx.from_address_profit_loss_xrp,
                        tx.from_address_roi_percentage
                      );
                      if (!metrics) {
                        return (
                          <span className="whitespace-nowrap text-body font-body text-subtext-color">
                            –
                          </span>
                        );
                      }
                      return (
                        <span className="whitespace-nowrap text-body font-body">
                          <span className="text-default-font">{metrics.holdText}</span>
                          {' '}
                          <span className={metrics.plColor}>{metrics.plText}</span>
                        </span>
                      );
                    })()}
                  </Table.Cell>
                </Table.Row>
              );
              })}
            </Table>
            </div>
          )}
        </div>
        </div>
      </DefaultPageLayout>

      {/* New transaction modal for buy flow */}
      {showTransactionModal && transactionFees && transactionNFTDetails && (
        <XamanTransactionModal
          type="buy"
          nft={transactionNFTDetails}
          fees={transactionFees}
          stage={transactionStage}
          onStageChange={setTransactionStage}
          payload={xamanPayload ?? undefined}
          txHash={txHash}
          error={actionError || undefined}
          onConfirm={handleConfirmTransaction}
          onSuccess={handleTransactionSigned}
          onError={(errorMsg) => {
            setActionError(errorMsg);
            setTransactionStage('error');
          }}
          onClose={() => {
            setShowTransactionModal(false);
            setTransactionStage('confirmation');
            setXamanPayload(null);
            setTransactionFees(null);
            setTransactionNFTDetails(null);
            setTxHash(undefined);
            setActionError(null);
          }}
          isGuest={!isAuthenticated}
        />
      )}

      {/* Xaman modal for sell/transfer/cancel flows */}
      {showXamanModal && xamanPayload && (
        <WalletSigningModal
          open={showXamanModal}
          onClose={() => {
            setShowXamanModal(false);
            setProcessing(false);
            setXamanPayload(null);
            setCurrentOperation(null);
            setActionError(null);
          }}
          onSuccess={(txHash) => {
            console.log('Transaction signed successfully:', txHash);

            // Use existing transaction handling logic which includes polling
            // and shows toast notification on success
            handleTransactionSigned(txHash);
          }}
          onRejected={() => {
            console.log('Transaction rejected by user');
            setActionError('Transaction was rejected');
            setProcessing(false);
          }}
          onExpired={() => {
            console.log('Transaction expired');
            setActionError('Transaction request expired. Please try again.');
            setProcessing(false);
          }}
          title={getModalTitle()}
          payload={xamanPayload}
          message={getModalMessage()}
        />
      )}

      {/* Accept Transfer Offer Modal */}
      {showTransferSigningModal && transferPayload && (
        <WalletSigningModal
          open={showTransferSigningModal}
          onClose={() => {
            setShowTransferSigningModal(false);
            setTransferPayload(null);
          }}
          onSuccess={handleTransferSuccess}
          onRejected={handleTransferRejected}
          onExpired={handleTransferExpired}
          title="Accept Transfer"
          payload={transferPayload}
          message="Sign to accept this NFT transfer"
        />
      )}

      {showCancelTransferModal && cancelTransferPayload && (
        <WalletSigningModal
          open={showCancelTransferModal}
          onClose={() => {
            setShowCancelTransferModal(false);
            setCancelTransferPayload(null);
          }}
          onSuccess={handleCancelTransferSuccess}
          onRejected={handleCancelTransferRejected}
          onExpired={() => {
            setShowCancelTransferModal(false);
            setCancelTransferPayload(null);
            setActionError('Transaction expired. Please try again.');
          }}
          title="Cancel Transfer"
          payload={cancelTransferPayload}
          message="Sign to cancel this transfer offer"
        />
      )}

      {/* Promotion Dialog */}
      {isOwner && user?.xrpl_address && (
        <PromotionDialog
          isOpen={showPromotionDialog}
          onClose={() => setShowPromotionDialog(false)}
          nftTokenId={nft.nft_token_id}
          nftName={nft.name || `NFT #${nft.token_id}`}
          userAddress={user.xrpl_address}
          onSuccess={() => {
            // Refresh promotion status after successful promotion
            getPromotionStatus(nft.nft_token_id).then((response) => {
              if (response.success && response.data) {
                setPromotionStatus(response.data);
              }
            });
          }}
        />
      )}

      {/* Metadata History Modal (dNFT) */}
      {nft.is_mutable && (
        <MetadataHistoryModal
          isOpen={showMetadataHistory}
          onClose={() => setShowMetadataHistory(false)}
          nftTokenId={nft.nft_token_id}
          nftName={nft.name || `NFT #${nft.token_id}`}
          currentVersion={nft.metadata_version || 1}
          currentNFT={{
            name: nft.name,
            description: nft.description,
            image_url: nft.image_url,
            media_url: nft.media_url || null,
            thumbnail_url: nft.thumbnail_url || null,
            metadata_uri: nft.metadata_uri || null,
            metadata: (nft as any).metadata || null,
            traits: nft.traits || null,
            metadata_updated_at: nft.metadata_updated_at || null,
          }}
          nftCreatedAt={nft.created_at}
        />
      )}

      {/* Accept Collection Offer Modal (Sell Now) */}
      {showAcceptCollectionOfferModal && bestCollectionOffer && nft && (
        <AcceptCollectionOfferModal
          isOpen={showAcceptCollectionOfferModal}
          onClose={() => setShowAcceptCollectionOfferModal(false)}
          offer={bestCollectionOffer}
          userNfts={[{
            nftTokenId: nft.nft_token_id,
            name: nft.name || `NFT #${nft.token_id}`,
            imageUrl: nft.image_url,
            thumbnailUrl: nft.thumbnail_url,
            traits: nft.traits,
          }]}
          collectionName={nft.collection?.name || 'Collection'}
          onSuccess={() => {
            setShowAcceptCollectionOfferModal(false);
            setBestCollectionOffer(null);
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId!) });
            queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId!) });
          }}
        />
      )}

      {/* Accept Buy Offer Modal */}
      {selectedBuyOffer && nft && (
        <AcceptBuyOfferModal
          isOpen={showAcceptBuyOfferModal}
          onClose={() => {
            setShowAcceptBuyOfferModal(false);
            setSelectedBuyOffer(null);
          }}
          offer={selectedBuyOffer}
          nft={{
            nft_token_id: nft.nft_token_id,
            name: nft.name || 'Unnamed NFT',
            thumbnail_url: nft.thumbnail_url,
          }}
          onSuccess={() => {
            setShowAcceptBuyOfferModal(false);
            setSelectedBuyOffer(null);
            queryClient.invalidateQueries({ queryKey: nftQueryKeys.offers(tokenId!) });
            queryClient.invalidateQueries({ queryKey: nftQueryKeys.nft(tokenId!) });
            showSuccess('Offer accepted! NFT sold.');
          }}
        />
      )}

      {/* Tag Edit Modal */}
      {showTagEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowTagEditModal(false)}
          />
          {/* Modal */}
          <div
            className="relative z-10 w-full max-w-lg mx-4 rounded-lg border border-neutral-border bg-default-background shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-border">
              <h3 className="text-heading-3 font-heading-3 text-default-font">
                Edit Collection Tags
              </h3>
              <IconButton
                variant="neutral-tertiary"
                size="small"
                icon={<FeatherX />}
                onClick={() => setShowTagEditModal(false)}
              />
            </div>
            {/* Body */}
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-4">
                {collectionTags.map((tag) => (
                  <div key={tag.id} className="flex flex-col gap-2">
                    <label className="text-body-bold font-body-bold text-default-font">
                      {tag.display_name}
                      {tag.description && (
                        <span className="text-caption font-caption text-subtext-color ml-2">
                          ({tag.description})
                        </span>
                      )}
                    </label>
                    {tag.tag_type === 'boolean' ? (
                      <select
                        value={editingTags[tag.id] || ''}
                        onChange={(e) => setEditingTags({ ...editingTags, [tag.id]: e.target.value })}
                        className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                      >
                        <option value="">-- Not set --</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : tag.tag_type === 'number' ? (
                      <input
                        type="number"
                        step="any"
                        value={editingTags[tag.id] || ''}
                        onChange={(e) => setEditingTags({ ...editingTags, [tag.id]: e.target.value })}
                        placeholder="Enter a number"
                        className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    ) : tag.tag_type === 'url' ? (
                      <input
                        type="url"
                        value={editingTags[tag.id] || ''}
                        onChange={(e) => setEditingTags({ ...editingTags, [tag.id]: e.target.value })}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    ) : (
                      <input
                        type="text"
                        value={editingTags[tag.id] || ''}
                        onChange={(e) => setEditingTags({ ...editingTags, [tag.id]: e.target.value })}
                        placeholder="Enter text"
                        className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Footer */}
            <div className="flex items-center justify-end gap-2 p-4 border-t border-neutral-border">
              <Button
                variant="neutral-secondary"
                onClick={() => setShowTagEditModal(false)}
                disabled={savingTags}
              >
                Cancel
              </Button>
              <Button
                variant="brand-primary"
                onClick={handleSaveTags}
                loading={savingTags}
              >
                Save Tags
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Trade Builder Modal */}
      {showTradeBuilder && nft?.owner_address && (
        <TradeBuilder
          isOpen={showTradeBuilder}
          onClose={() => setShowTradeBuilder(false)}
          onTradeCreated={() => {
            setShowTradeBuilder(false);
            showSuccess('Trade proposal sent!');
          }}
          recipientAddress={nft.owner_address}
          preSelectedRecipientNfts={tokenId ? [{
            nft_token_id: tokenId,
            name: nft.name,
            image_url: nft.image_url,
            thumbnail_url: nft.thumbnail_url,
            rarity_rank: nft.rarity_rank,
            collection_name: nft.collection?.name,
            collection_id: nft.collection_id,
          }] : undefined}
        />
      )}
    </>
  );
};
